import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { laneTimeout } from './api/agentLane';
import type { LaneImageRef } from './components/chat/LaneImages';
import Login from './components/Login';
import type { Thread } from './components/ChatThreads';
import SecurityPage from './components/SecurityPage';
import CodePage from './components/CodePage';
import TradingPage, { type ArthurSessionContext } from './components/TradingPage';
import RexyHomepage from './components/RexyHomepage';
import { reconcileRexyMessages, resolveActiveThreadId } from './lib/rexyChatReconcile';
import { openTodaysChat } from './lib/rexyDayChat';
import { governedMemoryStatusFromResponse, type MemoryStatus } from './lib/memoryStatus';
import { isMackTextTurn, newMackRequestId, mackCurrentResponse, mergeMackHistory, type MackTurn } from './lib/mackCurrentTurn';
import RexyPinScreen from './components/RexyPinScreen';
import PhoneHost from './components/phone/PhoneHost';
import RexyGraphLab from './components/graph/dev/RexyGraphLab';
import { currentGraphSourceMode } from './components/graph/graphSourceMode';
import AppShell from './components/shell/AppShell';
import SettingsPage from './components/settings/SettingsPage';
import DomainDashboardScreen from './components/domains/DomainDashboardScreen';
import AgentViewScreen from './components/agents/AgentViewScreen';
import { dashboardIsMissionControl } from './components/domains/DomainDashboardScreen';
import { Nav, INITIAL_NAV } from './components/missioncontrol/navigation';
// ChatSurface itself is mounted inside AgentViewScreen — App.tsx only references its
// props contract for the chatSurfaceProps bag. Type-only import keeps it out of the
// runtime graph (and out of the unused-vars warning set).
import type { ChatSurfaceProps } from './components/chat/ChatSurface';

type ArthurSurface = 'HOME' | 'LIVE_SESSION' | 'BACKTESTING' | 'LEDGER' | 'DREAMS_AND_JOURNALS' | 'DEEP_EXPERTISE';

// Strip markdown formatting — Qwen responds in markdown
function stripMarkdown(text: string): string {
  return text
    .replace(/#{1,6}\s+/g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/^[-]\s+/gm, '• ')
    .replace(/---+/g, '')
    .replace(/\n\n\n+/g, '\n\n')
    .trim();
}
// 2.11 — Token in memory only, not localStorage
declare global { interface Window { __rexyToken: string; } }
window.__rexyToken = window.__rexyToken || '';

// Which surface this document is: the landscape desktop, or the portrait phone
// (?surface=phone). Read once at module load — it never changes within a document.
const PHONE_SURFACE =
  new URLSearchParams(window.location.search).get('surface') === 'phone';

// The development-only spatial-graph lab (?surface=graphlab, owner ruling). Same
// mechanism as the phone surface, read once at module load. It is NOT the
// production GRAPH tab — that tab stays empty until G3.
const GRAPH_LAB_SURFACE =
  new URLSearchParams(window.location.search).get('surface') === 'graphlab';


// Same-origin API base, keyed on the PAGE PROTOCOL (not NODE_ENV — the CRA dev
// server is NODE_ENV=development even when reached over an HTTPS front, so keying on
// the env appended :5000 under HTTPS and pointed at nothing).
//   • HTTPS page → same-origin on :443, no explicit port. Covers BOTH herexy.com
//     (nginx proxies /api → Flask) AND Tailscale Serve (path-mounts /api → Flask).
//     A browser on an HTTPS page also refuses to open ws:// — the socket goes wss
//     same-origin (useRexySocket), so both transports agree.
//   • HTTP page → direct to Flask on :5000, exactly as the countersigned LAN path
//     (and Electron on localhost) already work. Unchanged — do not regress.
const API_BASE = window.location.protocol === 'https:'
  ? `https://${window.location.host}/api`
  : `${window.location.protocol}//${window.location.hostname}:5000/api`;

// Axios interceptor — automatically adds auth token to every request
axios.interceptors.request.use(config => {
  // 2.11 — Token in memory only, sent as Authorization Bearer
  // Never stored in localStorage (XSS protection)
  const token = window.__rexyToken || '';
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
    // Keep X-Rexy-Token during transition period
    config.headers['X-Rexy-Token'] = token;
  }
  return config;
});

export interface Message {
  id: string;
  mackTurn?: MackTurn;
  // RECITE-WIRING-DELTA — 'system' is the INTERFACE voice: errors/statuses the
  // UI reports itself. No source-authored string is ever attributed to Rexy or
  // an agent; those render through ChatWindow's system-notice branch.
  sender: 'user' | 'rexy' | 'system';
  text: string;
  timestamp: string;
  source?: string;
  memoryStatus?: MemoryStatus;
  // System notices only — 'error' vs 'status' picks the notice accent.
  severity?: 'error' | 'status';
  // F1 Build B — non-empty only when local (Qwen) was down and the backend fell
  // back to the API to keep the OS alive. Rendered as a subtle amber inline notice.
  fallbackNotice?: string;
  // Frontier lanes (Phase 4) — the images a frontier turn carried or made: the
  // reply's `images`, or a history row's. Absent on every other message.
  images?: LaneImageRef[];
}

// RECITE-WIRING-DELTA — SYSTEM notice constructor. Every hardcoded/interface
// string in chat goes through here: sender 'system', never 'rexy'. Ephemeral UI
// state only — never sent to the backend, never persisted as an agent message.
// Server-persisted system rows are chat context, not agent speech. The reconciler
// keeps `sys_*` ids alongside the optimistic tail (lib/rexyChatReconcile.js).
function systemNotice(text: string, severity: 'error' | 'status' = 'error'): Message {
  return {
    id: `sys_${severity}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    sender: 'system',
    severity,
    text,
    timestamp: new Date().toLocaleTimeString(),
  };
}

type AgentEventEnvelope = {
  target_domain?: unknown;
  target_agent?: unknown;
  target_surface?: unknown;
  target_thread?: unknown;
  recipient?: unknown;
  event_type?: unknown;
  handover_authorised?: unknown;
};

type AgentEventScope = {
  domain: unknown;
  agent: unknown;
  surface: unknown;
  thread?: unknown;
  recipient?: unknown;
};

function scopeNorm(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

function agentEventAllowed(event: AgentEventEnvelope | null | undefined, active: AgentEventScope): boolean {
  if (!event) return false;
  const required = [event.target_domain, event.target_agent, event.target_surface, event.recipient, event.event_type];
  if (required.some((value) => !scopeNorm(value))) return false;
  const eventType = scopeNorm(event.event_type);
  if (event.handover_authorised && eventType !== 'HANDOVER') return false;
  if (eventType === 'HANDOVER' && !event.handover_authorised) return false;
  if (scopeNorm(event.target_domain) !== scopeNorm(active.domain)) return false;
  if (scopeNorm(event.target_agent) !== scopeNorm(active.agent)) return false;
  if (scopeNorm(event.recipient) !== scopeNorm(active.recipient || 'OWNER')) return false;
  if (scopeNorm(event.target_surface) !== scopeNorm(active.surface)) return false;
  const targetThread = String(event.target_thread ?? '').trim();
  if (targetThread && targetThread !== String(active.thread ?? '').trim()) return false;
  return true;
}

function senderFromHistoryRole(role: unknown): Message['sender'] {
  const normalized = String(role || '').toLowerCase().trim();
  if (normalized === 'user') return 'user';
  if (normalized === 'system' || normalized === 'context') return 'system';
  return 'rexy';
}

export interface Status {
  domain: string;
  agent: string | null;
  override_mode: boolean;
  frontier_api: string;
  frontier_target: string;
  brain_mode: string;
}

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSecurityPage, setIsSecurityPage] = useState(false);
  const [showSecurityDashboard, setShowSecurityDashboard] = useState(false);
  const [showCodeDashboard, setShowCodeDashboard] = useState(false);
  const [showArthurDashboard, setShowArthurDashboard] = useState(false);
  // Chunk 6 — screen-mode routing. Replaces Chunk 5's showHome boolean; 'home'
  // initial mirrors `showHome: true`. Transitions are UI state only (Flag 10):
  // back chevron + Home pill never call handleDomainChange or handleAgentChange.
  const [screen, setScreen] = useState<'home' | 'domain' | 'agent'>('home');
  // WHERE THE OPERATOR IS INSIDE MISSION CONTROL — held here, above the screen
  // switch, because that surface unmounts on the trip to chat. Kept here, the
  // position survives the round trip and DASHBOARD returns to the exact agent,
  // root, sub-tab and view that was left. Held there, it was destroyed on unmount
  // and the surface reopened at the domain landing every time.
  const [missionNav, setMissionNav] = useState<Nav>(INITIAL_NAV);

  // ── LIVE PREVIEW — App-level so it works from any chat view ──────────────
  // When ◈ preview is clicked in ChatWindow, this fires and auto-opens
  // the CODE workspace with the preview pane active.
  const [previewPayload, setPreviewPayload] = useState<{ code: string; filename: string; lang: string } | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const { code, filename, lang } = (e as CustomEvent).detail;
      setPreviewPayload({ code, filename, lang });
      setShowCodeDashboard(true);   // Auto-open CODE workspace
      setShowArthurDashboard(false);
    };
    window.addEventListener('rexy-preview', handler);
    return () => window.removeEventListener('rexy-preview', handler);
  }, []);
  const [authError, setAuthError] = useState('');


  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('open') === 'arthur') {
      setShowCodeDashboard(false);
      setShowSecurityDashboard(false);
      setShowArthurDashboard(true);
      setShowSettings(false);
    }
  }, [isAuthenticated]);
  // SETTINGS is a VIEW FLAG, never a domain value. Opening it leaves activeDomain
  // exactly as it was, which is what makes the same page mirror into every domain.
  const [showSettings, setShowSettings] = useState(false);
  const [activeDomain, setActiveDomain] = useState('PERSONAL');
  const hasUserSetDomain = React.useRef(false);
  const [activeAgent, setActiveAgent] = useState<string | null>(null);
  const [overrideMode, setOverrideMode] = useState(false);
  // Override API selector — null=off, string=forced API name
  const [overrideApi, setOverrideApi] = useState<string | null>(null);
  const [showOverrideMenu, setShowOverrideMenu] = useState(false);
  // THE OS-WIDE ESCALATION TOGGLE (owner ruling 2026-07-28). Two states, DEFAULT OFF.
  //   OFF → nothing reaches the API. ON → exactly what the old AUTO did.
  // The three-state selector is retired: `frontier_only` is NOT a choice anyone makes.
  // It named the state the system ENTERS when the local lane is down, which the backend
  // resilience floor already handles and which this surface REPORTS via localLaneUp.
  //
  // THIS IS NOT LOCAL UI STATE. The setting lives server-side (core/escalation, persisted
  // in memory/rexy_settings.json) because it is OS-wide — socket, voice, upload, the CLI
  // and Spencer's Drive all inherit it at the routing floor without passing anything.
  // We START at false and then READ the server: a toggle that renders its own guess is a
  // surface asserting something it has not verified. Default and fail-closed are both OFF.
  const [escalation, setEscalation] = useState<boolean>(false);
  const [escalationLoaded, setEscalationLoaded] = useState<boolean>(false);
  const [localLaneUp, setLocalLaneUp] = useState<boolean>(true);
  // The wire contract is unchanged — the backend still receives routing_mode; it is now
  // DERIVED from the toggle rather than chosen, and the backend normalises it regardless.
  const routingMode: 'local_only' | 'local_frontier' =
    escalation ? 'local_frontier' : 'local_only';
  // D4 — local-down fallback target. Drives the `fallback_target` field on every
  // /api/query POST (locked wire contract). When the LOCAL model (Qwen) is down,
  // the OS falls back to this target so it never goes dark. Independent of the
  // three-state routing switch: routing decides IF/HOW the frontier is used in
  // normal operation; this decides WHERE we land when local is unavailable.
  //   claude — fall back to Claude (DEFAULT — resilience: keeps the OS alive)
  //   gpt    — fall back to GPT
  //   none   — no fallback (air-gap / true local-only): local down → clean dead-end
  const [fallbackTarget, setFallbackTarget] =
    useState<'claude' | 'gpt' | 'none'>('claude');
  // Model selector — per agent default model
  // All available models — add new ones here as they release
  const ALL_MODELS = [
    'nvidia/Qwen3.6-35B-A3B-NVFP4',
    'claude-haiku-4-5',
    'claude-sonnet-4-6',
    'claude-opus-4-7',
    'gpt-5.4',
    'gpt-5.4-mini',
    'gpt-5.1-codex',
    'gpt-5.1-codex-mini',
    'gpt-5.5',
  ];
  // All agents get all models — easy to extend
  const AGENT_MODELS: Record<string, string[]> = {
    'default': ALL_MODELS,
  };
  // All agents default to local Qwen
  const DEFAULT_MODELS: Record<string, string> = {
    'default': 'nvidia/Qwen3.6-35B-A3B-NVFP4',
  };
  const [activeModel, setActiveModel] = useState<string>('nvidia/Qwen3.6-35B-A3B-NVFP4');
  const [showModelMenu, setShowModelMenu] = useState(false);
  // Security dashboard
  const [showSecurityDash, setShowSecurityDash] = useState(false);
  const [securityFlags, setSecurityFlags] = useState<any[]>([]);
  const [securitySummary, setSecuritySummary] = useState<any>({high:0,medium:0,low:0});
  const [securityLevel, setSecurityLevel] = useState<string>('medium');
  // Memory manager
  const [showMemoryManager, setShowMemoryManager] = useState(false);
  const [memoryEntries, setMemoryEntries] = useState<any[]>([]);
  const [memoryDomain, setMemoryDomain] = useState<string>('PERSONAL');
  const [memoryFilter, setMemoryFilter] = useState<string>('all');
  const [lastMemoryStatus, setLastMemoryStatus] = useState<MemoryStatus>(null);
  // Memory override — separate from API override
  // Controls whether governance contradiction check runs
  const [memoryOverride, setMemoryOverride] = useState(false);
  // CI-6 (post-correction): `sidebarOpen` is now the iOS-shape ChatThreadsBar
  // collapse state — true = expanded (240px), false = collapsed (44px). The
  // agent overlay was removed entirely (agent selection lives only in the
  // domain dashboard agent cards). Default collapsed so chat opens minimal.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Thread state — per agent
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [arthurActiveSurface, setArthurActiveSurface] = useState<ArthurSurface>('HOME');
  const [arthurSessionContext, setArthurSessionContext] = useState<ArthurSessionContext | null>(null);
  useEffect(() => {
    if (activeDomain !== 'TRADING' || activeAgent?.toLowerCase() !== 'arthur') return;
    let current = true;
    axios.get(`${API_BASE}/arthur/dashboard`, { params: { thread_id: activeThreadId } })
      .then(res => { if (current && res.data?.data?.selectedContext) setArthurSessionContext(res.data.data.selectedContext); })
      .catch(() => {});
    return () => { current = false; };
  }, [activeDomain, activeAgent, activeThreadId]);
  const [threads, setThreads] = useState<Thread[]>([]);
  // Separate chat history per domain+agent+thread combination
  const [allMessages, setAllMessages] = useState<Record<string, Message[]>>({});

  // Derive current chat key from active domain + agent + thread
  const chatKey = activeThreadId
    ? `${activeDomain}_${activeAgent || 'CEO'}_${activeThreadId}`
    : `${activeDomain}_${activeAgent || 'CEO'}`;
  const messages = allMessages[chatKey] || [];
  // Queued responses must be authorised against the scope at delivery.
  const activeEventScopeRef = useRef<AgentEventScope>({domain: activeDomain, agent: activeAgent, surface: arthurActiveSurface});
  activeEventScopeRef.current = {
    domain: activeDomain,
    agent: activeAgent,
    surface: activeDomain === 'TRADING' && activeAgent?.toLowerCase() === 'arthur'
      ? arthurActiveSurface : 'CHAT',
    thread: activeThreadId,
    recipient: 'OWNER',
  };
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);

  // ── 6.8 — HOMEPAGE/REXY persistent chat slice ──────────────────────────────
  // SINGLE-SOURCE pair constants: the homepage chat is ALWAYS {HOMEPAGE, REXY},
  // bound atomically and NEVER derived from the nav state (activeDomain/activeAgent),
  // which on the homepage is {PERSONAL, null} — the project_domain_agent_pair_drift
  // failure. This matches the AppShell PTT binding, so 4.5 voice transcripts (written
  // canonically to the HOMEPAGE/REXY thread) auto-render here, and sends route through
  // the existing 6.3-validated /api/query → _rexy_live_turn path.
  const REXY_HOME_DOMAIN = 'HOMEPAGE';
  const REXY_HOME_AGENT = 'REXY';
  const [rexyThreads, setRexyThreads] = useState<Thread[]>([]);
  const [rexyActiveThreadId, setRexyActiveThreadId] = useState<string | null>(null);
  /** ── DID THE USER CHOOSE THIS THREAD, OR DID WE RESOLVE IT? ────────────────
   *
   *  The HOME/REXY thread was resolved ONCE, on auth, and then polled forever by
   *  that fixed id. A thread created later BY THE DEVICE — every voice turn
   *  writes through the daemon bridge, which resolves its own thread — was
   *  therefore invisible here: iOS could see desktop's messages, because it
   *  polled the thread desktop had pinned, while desktop was never subscribed to
   *  a thread the device created. Nothing was ever lost; the desktop was looking
   *  at an older thread.
   *
   *  So: a thread we resolved is FOLLOWED (re-resolved as the server's active
   *  thread moves), and a thread the user picked or created is PINNED and left
   *  exactly where they put it. A surface that yanks the user off the thread
   *  they deliberately opened would be a worse bug than the one this fixes. */
  const rexyThreadPinnedRef = useRef(false);
  const [rexyLoading, setRexyLoading] = useState(false);
  const rexyChatKey = rexyActiveThreadId
    ? `${REXY_HOME_DOMAIN}_${REXY_HOME_AGENT}_${rexyActiveThreadId}`
    : `${REXY_HOME_DOMAIN}_${REXY_HOME_AGENT}`;
  const rexyMessages = allMessages[rexyChatKey] || [];

  // Close override dropdown when clicking outside
  useEffect(() => {
    if (!showOverrideMenu) return;
    const handler = () => setShowOverrideMenu(false);
    // Delay to prevent immediate close on the opening click
    const timer = setTimeout(() => {
      document.addEventListener('click', handler);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handler);
    };
  }, [showOverrideMenu]);

  React.useEffect(() => {
    if (!showModelMenu) return;
    const handler = () => setShowModelMenu(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showModelMenu]);

  // Poll status every 5 seconds when authenticated
  // Load conversation history when authenticated — works on any device on login
  // P1b — nav-slice load is for domain/agent views only; never the homepage. On the
  // home screen the {HOMEPAGE, REXY} slice loads instead (effect below). At login
  // screen==='home', so this no longer fires the premature PERSONAL/SPENCER load;
  // navigation handlers (handleDomainChange/handleAgentChange) load nav threads.
  useEffect(() => {
    if (!isAuthenticated || screen === 'home') return;
    setTimeout(() => {
      loadThreads(activeDomain, activeAgent || 'SPENCER');
    }, 800);
  }, [isAuthenticated]);

  // 6.8 — load the persistent HOMEPAGE/REXY thread on auth (independent of nav).
  useEffect(() => {
    if (!isAuthenticated) return;
    const t = setTimeout(() => { loadRexyThreads(); }, 900);
    return () => clearTimeout(t);
  }, [isAuthenticated]);

  // ESCALATION — READ THE SERVER ON LOAD. The toggle must reflect real OS state, never
  // a local guess, and it must survive a reload (owner ruling: it is an OS setting, not
  // a browser preference). Any failure leaves it OFF — the fail-closed direction, matching
  // the backend, so an unreachable server can never render as "escalation on".
  // NOTE: the token is read inline from window rather than the `authToken` const declared
  // further down — these callbacks sit above it, and the axios interceptor uses the same
  // source, so there is one token origin, not two.
  const refreshEscalation = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/rexy/escalation`, {
        headers: { Authorization: `Bearer ${(window as any).__rexyToken || ''}` },
      });
      setEscalation(res.data?.escalation === true);
      setLocalLaneUp(res.data?.local_lane_up !== false);
    } catch {
      setEscalation(false);          // unreadable ⇒ OFF, never an optimistic ON
      setLocalLaneUp(true);
    } finally {
      setEscalationLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    refreshEscalation();
    // Re-read periodically so the OFFLINE state is reported, not guessed: the local lane
    // can drop while the page is open, and the surface must follow the box, not the click.
    const iv = setInterval(refreshEscalation, 30000);
    return () => clearInterval(iv);
  }, [isAuthenticated, refreshEscalation]);

  // Toggle: POST, then take the SERVER's answer as truth. Never optimistic — if the write
  // fails the toggle must not show a state the OS is not in.
  const toggleEscalation = useCallback(async (next: boolean) => {
    try {
      const res = await axios.post(`${API_BASE}/rexy/escalation`, { escalation: next }, {
        headers: { Authorization: `Bearer ${(window as any).__rexyToken || ''}` },
      });
      setEscalation(res.data?.escalation === true);
    } catch {
      await refreshEscalation();     // failed write ⇒ resync to whatever the OS actually holds
    }
  }, [refreshEscalation]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API_BASE}/status`, {
          withCredentials: true,
        });
        setStatus(response.data);
        if (!hasUserSetDomain.current) {
          setActiveDomain(response.data.domain);
          setActiveAgent(response.data.agent);
        }
        setOverrideMode(response.data.override_mode);
        setMemoryOverride(response.data.memory_override || false);
      } catch (error) {
        console.error('Status fetch failed:', error);
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // ── Message polling — keeps active thread current on VPS ──────────────────
  // Polls every 10 seconds for new messages in the active thread.
  // On VPS: picks up messages pushed from local in real time.
  // On local: picks up messages from VPS chats.
  // Only polls when authenticated and a thread is active.
  // Silent — no visual disruption, just appends new messages.
  useEffect(() => {
    // P1b — the nav-slice poll must NOT run on the home screen (the /status poll sets
    // activeAgent=SPENCER even on home, which otherwise polls PERSONAL/SPENCER there).
    // The homepage polls the {HOMEPAGE, REXY} slice instead (effect below).
    if (!isAuthenticated || !activeDomain || !activeAgent || screen === 'home') return;

    const pollMessages = async () => {
      try {
        const rexyToken = window.__rexyToken || '';
        const params: any = { domain: activeDomain, agent: activeAgent };
        if (activeThreadId) params.thread_id = activeThreadId;
        const res = await axios.get(`${API_BASE}/conversation`, {
          params,
          withCredentials: true,
          headers: { 'X-Rexy-Token': rexyToken },
        });
        const data = res.data;
        const tid = activeThreadId || data.thread_id;
        const key = tid
          ? `${activeDomain}_${activeAgent}_${tid}`
          : `${activeDomain}_${activeAgent}`;

        if (isMackTextTurn(activeDomain, activeAgent) && Array.isArray(data.messages)) {
          setAllMessages(prev => ({
            ...prev, [key]: mergeMackHistory(data.messages, prev[key] || [], senderFromHistoryRole),
          }));
          return;
        }

        if (data.messages && data.messages.length > 0) {
          setAllMessages(prev => {
            const existing = prev[key] || [];

            // Count only restored messages (not welcome/pending/uploading)
            const existingRestored = existing.filter(m =>
              m.id.startsWith('restored_') ||
              (!m.id.startsWith('welcome_') && !m.id.startsWith('pending_') && !m.id.startsWith('uploading_'))
            );

            // Only update if server has strictly more messages
            if (data.messages.length <= existingRestored.length) return prev;

            // Append only genuinely new messages from server
            // Find messages that don't exist in current state by fingerprint
            const existingFingerprints = new Set(
              existingRestored.map((m: Message) =>
                `${m.sender}:${m.text.slice(0, 50)}`
              )
            );

            const newMessages: Message[] = [];
            data.messages.forEach((msg: any, i: number) => {
              const sender = senderFromHistoryRole(msg.role);
              const fp = `${sender}:${String(msg.content).slice(0, 50)}`;
              if (!existingFingerprints.has(fp)) {
                newMessages.push({
                  id: `restored_${Date.now()}_${i}`,
                  sender,
                  text: msg.content,
                  timestamp: '',
                  source: msg.source || 'LOCAL_AI',
                  memoryStatus: msg.memoryStatus || null,
                  ...(msg.images ? { images: msg.images } : {}),
                });
              }
            });

            if (newMessages.length === 0) return prev;

            // Insert new messages before welcome messages
            const welcomes = existing.filter((m: Message) => m.id.startsWith('welcome_'));
            const nonWelcome = existing.filter((m: Message) => !m.id.startsWith('welcome_'));
            return { ...prev, [key]: [...nonWelcome, ...newMessages, ...welcomes] };
          });
        }
      } catch (e) {
        // Silent fail — polling is non-critical
      }
    };

    const pollInterval = setInterval(pollMessages, 10000);
    return () => clearInterval(pollInterval);
  }, [isAuthenticated, activeDomain, activeAgent, activeThreadId, API_BASE, screen]);

  // ── [P1b] HOMEPAGE/REXY home poll ────────────────────────────────────────────
  // While on the home screen, poll the {HOMEPAGE, REXY} conversation so 4.5 voice
  // transcripts (written canonically to that thread) auto-render and interleave with
  // text. Bound to the single-source pair only — never nav state. Reuses the SAME
  // fingerprint de-dupe as the nav poll so optimistic sends (handleRexySend) don't
  // double-render when the server copy arrives.
  useEffect(() => {
    if (!isAuthenticated || screen !== 'home') return;

    // ── FOLLOW THE SERVER'S ACTIVE THREAD (2026-08-10) ──────────────────────
    // The thread was resolved once, on auth, and then this poll used that id for
    // the life of the session. Voice turns write through the daemon bridge,
    // which resolves ITS OWN thread — so a thread the device created never
    // appeared here. Re-resolved on the poll cadence, and ONLY while the thread
    // is one we resolved: a user who opened a specific thread stays in it.
    const followActiveThread = async () => {
      if (rexyThreadPinnedRef.current) return;
      try {
        const res = await axios.get(`${API_BASE}/threads`, {
          params: { domain: REXY_HOME_DOMAIN, agent: REXY_HOME_AGENT },
          withCredentials: true,
        });
        const list: Thread[] = res.data.threads || [];
        const active = list.find(t => t.status === 'active');
        if (!active || active.id === rexyActiveThreadId) return;
        setRexyThreads(list);
        setRexyActiveThreadId(active.id);
        loadConversationHistory(REXY_HOME_DOMAIN, REXY_HOME_AGENT, active.id);
      } catch (e) {
        // Silent, like the message poll beside it: a failed re-resolve leaves the
        // surface on the thread it already had, which is the safe direction.
      }
    };

    const pollRexyMessages = async () => {
      // Stable-key guard: only poll once a thread is resolved, so the poll key always equals the
      // send/render key (no orphaning). reconcileRexyMessages CONVERGES the thread to the server's
      // ORDERED list + keeps the in-flight optimistic tail, deduped by normalized full content —
      // no count gate, no first-50-char fingerprint, no tail re-inject of prior turns.
      if (!rexyActiveThreadId) return;
      try {
        const rexyToken = window.__rexyToken || '';
        const key = `${REXY_HOME_DOMAIN}_${REXY_HOME_AGENT}_${rexyActiveThreadId}`;
        const res = await axios.get(`${API_BASE}/conversation`, {
          params: { domain: REXY_HOME_DOMAIN, agent: REXY_HOME_AGENT, thread_id: rexyActiveThreadId },
          withCredentials: true,
          headers: { 'X-Rexy-Token': rexyToken },
        });
        const data = res.data;
        if (!data || !Array.isArray(data.messages)) return;
        setAllMessages(prev => ({
          ...prev,
          [key]: reconcileRexyMessages(data.messages, prev[key] || []),
        }));
      } catch (e) {
        // Silent — polling is non-critical.
      }
    };

    const tick = async () => { await followActiveThread(); await pollRexyMessages(); };
    tick();
    const rexyPollInterval = setInterval(tick, 10000);
    return () => clearInterval(rexyPollInterval);
  }, [isAuthenticated, screen, rexyActiveThreadId, API_BASE]);

  const handleLogin = async (pin: string) => {
    try {
      const response = await axios.post(
        `${API_BASE}/auth`,
        { pin },
        { withCredentials: true }
      );
      if (response.data.success) {
        if (response.data.token) {
          window.__rexyToken = response.data.token;
        }
        setIsAuthenticated(true);
        setAuthError('');
        // P1b — no premature nav-slice load here: login lands on the homepage, which
        // loads the {HOMEPAGE, REXY} slice (loadRexyThreads). Nav threads load on
        // navigation to a domain/agent (handleDomainChange/handleAgentChange).
      }
    } catch (err: any) {
      // HONEST AUTH ERRORS: a REJECTED PIN and a FAILED REQUEST are different facts and
      // must not wear the same message. The old code labelled EVERY non-429 failure —
      // connection refused, CORS/preflight block, proxy 404, gateway 502 — as "Invalid
      // PIN", sending the owner chasing a credential problem that never existed while the
      // real cause was transport. Flask only ever rejects a PIN with HTTP 401
      // {error:"Invalid PIN"} (server/app.py:1757); anything without an auth-shaped
      // response never reached authenticate().
      const resp = err?.response;
      const status = resp?.status;
      const serverMsg = resp?.data?.error;
      if (!resp) {
        // No HTTP response at all — the POST never completed: network error, CORS
        // preflight block, connection refused, timeout. NOT a credential failure.
        setAuthError("Couldn't reach Rexy — the login request didn't get through. Check the connection and try again.");
      } else if (status === 401 && serverMsg === 'Invalid PIN') {
        // Flask ran authenticate() and rejected the credential — the one true wrong PIN.
        setAuthError('Invalid PIN. Try again.');
      } else if (status === 429) {
        // Rate / volume limit — surface Flask's own message.
        setAuthError(serverMsg || 'Too many attempts. Please wait a moment.');
      } else {
        // Flask (or a proxy) responded, but NOT with a credential rejection: a 500
        // session-persist error carries its own message; a 404/502 is a routing/gateway
        // failure, not a wrong PIN. Prefer the server's message, else name it as transport.
        setAuthError(serverMsg || `Couldn't reach Rexy — the server responded ${status}, not a login result. Try again.`);
      }
    }
  };

  const handleDomainChange = async (domain: string) => {
    // Compute target pair BEFORE any state writes (Ghost-mandated ordering).
    //
    // Rule: domains with an empty native agents array in lib/agents.ts get a
    // null default. Service agents (GRANT, MCGARRETT, JESSE) are NOT valid
    // domain defaults for this minimum-blast scope, because POST /api/agent
    // with a service-agent name hits the SERVICE_AGENTS branch in
    // ceo.set_agent and returns WITHOUT updating self.active_agent - the
    // client would commit activeAgent=GRANT while server stays on the prior
    // active_agent, causing silent divergence. Only domain-native agents are
    // bindable as active_agent on the server side.
    //
    //   FRONTIER: null - pipeline-only domain, no native agents
    //   RESEARCH: null - services-only domain, GRANT is a service agent
    //                    (would silently no-op server-side)
    const firstAgents: Record<string, string | null> = {
      PERSONAL: 'SPENCER', TRADING: 'ARTHUR', BUSINESS: 'STRATIQ',
      RESEARCH: null,     GROWTH:  'ALPHA',  FRONTIER: null,
      CODE:     'SPARTICUS', SECURITY: 'SANTI', MEDIA: 'HAMMOND'
    };
    const defaultAgent = firstAgents[domain] ?? null;

    // Incremental commit (Ghost Option I). Each POST gates only its own state
    // write - no atomic "both must succeed" coupling that would hide a
    // server/client divergence on partial failure.
    //   /api/domain failure: clean abort, local unchanged.
    //   /api/agent failure after /api/domain success: local activeDomain
    //     matches the now-switched server, local activeAgent stays null -
    //     chat UI shows "no agent selected" matching the partial reality.
    //     User can recover by manually picking an agent in the sidebar.

    try {
      await axios.post(
        `${API_BASE}/domain`,
        { domain },
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Domain change failed:', error);
      return;
    }

    // /api/domain succeeded - commit domain state. hasUserSetDomain flips
    // here (not earlier) so /status poll suppression aligns with actual local
    // mutation; if /api/domain had failed we'd want the poll to keep
    // reconciling against server.
    hasUserSetDomain.current = true;
    setShowArthurDashboard(false);
    setActiveDomain(domain);
    setActiveAgent(null);
    setActiveThreadId(null);
    setThreads([]);

    if (defaultAgent === null) {
      // Services-only domain - no domain-native default to bind. Done.
      return;
    }

    try {
      await axios.post(
        `${API_BASE}/agent`,
        { agent: defaultAgent },
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Agent default set failed after domain change:', error);
      return;  // activeAgent already null - honest partial state
    }

    // /api/agent succeeded - commit agent + load threads.
    setActiveAgent(defaultAgent);
    setTimeout(() => loadThreads(domain, defaultAgent), 300);
  };

  const loadThreads = async (domain: string, agent: string) => {
    try {
      const res = await axios.get(`${API_BASE}/threads`, {
        params: { domain, agent },
        withCredentials: true,
      });
      const threadList: Thread[] = res.data.threads || [];
      setThreads(threadList);
      // Set active thread to most recent active one
      const active = threadList.find(t => t.status === 'active');
      if (active) {
        setActiveThreadId(active.id);
        loadConversationHistory(domain, agent, active.id);
      }
    } catch (e) {
      console.error('Thread load failed:', e);
    }
  };

  // BUCKLY'S DAY CHATS (owner ruling 27 Oct 2026, B-2b): every day is her own chat, as
  // Rexy's and Arthur's are theirs, so New chat and End chat both take Matt to TODAY's
  // chat - read only: nothing is posted, nothing closed, no "Chat N" made and nothing
  // batched into trading.json. Today's chat is the server's (lib/rexyDayChat.js).
  const isBucklyChat = () =>
    activeDomain.toUpperCase() === 'TRADING' && (activeAgent || '').toUpperCase() === 'BUCKLY';
  const openBucklyToday = async () => {
    if (!activeAgent) return;
    try {
      const today = await openTodaysChat(axios.get, API_BASE, activeDomain, activeAgent,
                                         { 'X-Rexy-Token': window.__rexyToken || '' });
      if (!today) return;
      setThreads(today.threads);
      setActiveThreadId(today.threadId);
      loadConversationHistory(activeDomain, activeAgent, today.threadId);
    } catch (e) {
      console.error("Buckly today's chat failed:", e);
    }
  };

  // R12 (B-2e5): HER TURN SHOWS IN THE CHAT IT GOES INTO. The server puts each typed turn of hers into
  // today's chat, whichever chat the page shows (get_day_chat), and the day's first contact makes that chat;
  // the page used to draw the turn in the chat it had open. So before her turn is sent the server is asked
  // for today's chat as New chat asks it, and the page goes there, its messages loaded, if it shows another.
  // Null when the server cannot be asked: the page stays where it is.
  const bucklyTurnChat = async (): Promise<string | null> => {
    try {
      const today = await openTodaysChat(axios.get, API_BASE, activeDomain, activeAgent || '',
                                         { 'X-Rexy-Token': window.__rexyToken || '' });
      if (!today) return null;
      if (today.threadId !== activeThreadId) {
        await loadConversationHistory(activeDomain, activeAgent || '', today.threadId);
        setThreads(today.threads);
        setActiveThreadId(today.threadId);
      }
      return today.threadId;
    } catch (e) {
      console.error("Buckly's turn: today's chat not reached:", e);
      return null;
    }
  };

  const handleNewThread = async () => {
    if (!activeAgent) return;
    if (isBucklyChat()) { await openBucklyToday(); return; }
    try {
      if (activeDomain.toUpperCase() === 'TRADING' && activeAgent.toUpperCase() === 'ARTHUR') {
        const today = await openTodaysChat(axios.get, API_BASE, activeDomain, activeAgent,
                                         { 'X-Rexy-Token': window.__rexyToken || '' });
        if (today) {
          setThreads(today.threads);
          setActiveThreadId(today.threadId);
          loadConversationHistory(activeDomain, activeAgent, today.threadId);
        }
        return;
      }
      const res = await axios.post(`${API_BASE}/threads`, {
        domain: activeDomain,
        agent: activeAgent,
      }, { withCredentials: true });
      const newThread: Thread = res.data.thread;
      setThreads(prev => [newThread, ...prev]);
      setActiveThreadId(newThread.id);
      // Clear messages for new thread
      const newKey = `${activeDomain}_${activeAgent}_${newThread.id}`;
      setAllMessages(prev => ({ ...prev, [newKey]: [] }));
    } catch (e) {
      console.error('New thread failed:', e);
    }
  };

  const handleSelectThread = (threadId: string) => {
    setActiveThreadId(threadId);
    if (activeAgent) {
      loadConversationHistory(activeDomain, activeAgent, threadId);
    }
  };

  const handleEndThread = async (threadId: string) => {
    if (!activeAgent) return;
    if (isBucklyChat()) { await openBucklyToday(); return; }
    try {
      if (activeDomain.toUpperCase() === 'TRADING' && activeAgent.toUpperCase() === 'ARTHUR') {
        await handleNewThread();
        return;
      }
      const res = await axios.post(`${API_BASE}/threads/end`, {
        domain: activeDomain,
        agent: activeAgent,
        thread_id: threadId,
      }, { withCredentials: true });
      if (res.data.success) {
        // Update thread status locally
        setThreads(prev => prev.map(t =>
          t.id === threadId ? { ...t, status: 'closed' as const } : t
        ));
        // Switch to new thread
        const newThread: Thread = res.data.new_thread;
        setThreads(prev => [newThread, ...prev]);
        setActiveThreadId(newThread.id);
        const newKey = `${activeDomain}_${activeAgent}_${newThread.id}`;
        setAllMessages(prev => ({ ...prev, [newKey]: [] }));
      }
    } catch (e) {
      console.error('End thread failed:', e);
    }
  };

  const handleRenameThread = async (threadId: string, newName: string) => {
    if (!activeAgent) return;
    try {
      await axios.post(`${API_BASE}/threads/rename`, {
        domain: activeDomain,
        agent: activeAgent,
        thread_id: threadId,
        name: newName,
      }, { withCredentials: true });
      setThreads(prev => prev.map(t =>
        t.id === threadId ? { ...t, name: newName } : t
      ));
    } catch (e) {
      console.error('Rename thread failed:', e);
    }
  };

  const handleDeleteThread = async (threadId: string) => {
    if (!activeAgent) return;
    // R87 (B-2e): her Delete chat deletes nothing; like New chat, it opens today's chat.
    if (isBucklyChat()) { await openBucklyToday(); return; }
    try {
      await axios.post(`${API_BASE}/threads/delete`, {
        domain: activeDomain,
        agent: activeAgent,
        thread_id: threadId,
      }, { withCredentials: true });
      setThreads(prev => prev.filter(t => t.id !== threadId));
      // If deleted active thread, switch to next active
      if (threadId === activeThreadId) {
        const remaining = threads.filter(t => t.id !== threadId && t.status === 'active');
        if (remaining.length > 0) {
          setActiveThreadId(remaining[0].id);
          loadConversationHistory(activeDomain, activeAgent, remaining[0].id);
        } else {
          handleNewThread();
        }
      }
    } catch (e) {
      console.error('Delete thread failed:', e);
    }
  };

  const loadConversationHistory = async (domain: string, agent: string, thread_id?: string) => {
    try {
      const rexyToken = window.__rexyToken || '';
      const params: any = { domain, agent };
      if (thread_id) params.thread_id = thread_id;
      const res = await axios.get(`${API_BASE}/conversation`, {
        params,
        withCredentials: true,
        headers: { 'X-Rexy-Token': rexyToken },
      });
      const data = res.data;
      const tid = thread_id || data.thread_id;
      const key = tid ? `${domain}_${agent}_${tid}` : `${domain}_${agent}`;
      if (isMackTextTurn(domain, agent) && Array.isArray(data.messages)) {
        setAllMessages(prev => ({
          ...prev, [key]: mergeMackHistory(data.messages, prev[key] || [], senderFromHistoryRole),
        }));
        return;
      }
      if (data.messages && data.messages.length > 0) {
        const restored: Message[] = data.messages.map((msg: any, i: number) => ({
          id: `restored_${i}`,
          sender: senderFromHistoryRole(msg.role),
          text: msg.content,
          timestamp: '',
          source: 'LOCAL_AI',
          ...(msg.images ? { images: msg.images } : {}),
        }));
        setAllMessages(prev => ({ ...prev, [key]: restored }));
        if (data.was_inactive) {
          // RECITE-WIRING-DELTA — the inactivity notice is the INTERFACE
          // reporting state, not Rexy speaking. Keeps the `welcome_` id prefix:
          // the poll/reconciler pin these to the thread tail by that prefix.
          const welcomeMsg: Message = {
            id: `welcome_${Date.now()}`,
            sender: 'system',
            severity: 'status',
            text: 'This thread was inactive — earlier conversation restored.',
            timestamp: new Date().toLocaleTimeString(),
          };
          setAllMessages(prev => ({
            ...prev,
            [key]: [...(prev[key] || []), welcomeMsg]
          }));
        }
      }
    } catch (e) {
      // No history yet — start fresh
    }
  };

  // ── 6.8 HOMEPAGE/REXY handlers — bound to the single-source pair ONLY ───────
  // Every one targets {REXY_HOME_DOMAIN, REXY_HOME_AGENT}; none reads nav state.
  const loadRexyThreads = async () => {
    try {
      const res = await axios.get(`${API_BASE}/threads`, {
        params: { domain: REXY_HOME_DOMAIN, agent: REXY_HOME_AGENT },
        withCredentials: true,
      });
      const list: Thread[] = res.data.threads || [];
      setRexyThreads(list);
      const active = list.find(t => t.status === 'active');
      if (active) {
        setRexyActiveThreadId(active.id);
        loadConversationHistory(REXY_HOME_DOMAIN, REXY_HOME_AGENT, active.id);
      } else {
        loadConversationHistory(REXY_HOME_DOMAIN, REXY_HOME_AGENT);
      }
    } catch (e) {
      console.error('Rexy thread load failed:', e);
    }
  };

  // DAY CHATS (owner ruling 2026-10-16): every day is Rexy's own chat, so "New chat"
  // takes him to TODAY's chat — the one the server resolves — and creates no "Chat N".
  // Unpinned, so the poll keeps following today's chat (lib/rexyDayChat.js).
  const handleRexyNewThread = async () => {
    try {
      const today = await openTodaysChat(axios.get, API_BASE, REXY_HOME_DOMAIN, REXY_HOME_AGENT,
                                         { 'X-Rexy-Token': window.__rexyToken || '' });
      if (!today) return;
      rexyThreadPinnedRef.current = false;
      setRexyThreads(today.threads);
      setRexyActiveThreadId(today.threadId);
      loadConversationHistory(REXY_HOME_DOMAIN, REXY_HOME_AGENT, today.threadId);
    } catch (e) { console.error('Rexy new thread failed:', e); }
  };

  const handleRexySelectThread = (threadId: string) => {
    rexyThreadPinnedRef.current = true;   // the user chose it; stop following
    setRexyActiveThreadId(threadId);
    loadConversationHistory(REXY_HOME_DOMAIN, REXY_HOME_AGENT, threadId);
  };

  // Resolve a stable HOMEPAGE/REXY thread id BEFORE the first send so the optimistic-send key,
  // the render key, and the poll key are all the same (turn-binding fix). If a thread is already
  // active, reuse it; otherwise create one and adopt it.
  const ensureRexyThread = async (): Promise<string | null> => {
    if (rexyActiveThreadId) return rexyActiveThreadId;
    try {
      // REUSE the existing active thread first (mirror loadRexyThreads' resolution) so a send
      // before the 900ms loadRexyThreads completes binds to the SAME thread — never fragments the
      // conversation. Create a new thread ONLY when the server reports no active one.
      const res = await axios.get(`${API_BASE}/threads`, {
        params: { domain: REXY_HOME_DOMAIN, agent: REXY_HOME_AGENT },
        withCredentials: true,
      });
      const list: Thread[] = res.data.threads || [];
      setRexyThreads(list);
      const activeId = resolveActiveThreadId(list);
      if (activeId) {
        setRexyActiveThreadId(activeId);
        return activeId;
      }
      const created = await axios.post(`${API_BASE}/threads`, {
        domain: REXY_HOME_DOMAIN, agent: REXY_HOME_AGENT,
      }, { withCredentials: true });
      const nt: Thread = created.data.thread;
      setRexyThreads(prev => [nt, ...prev]);
      setRexyActiveThreadId(nt.id);
      return nt.id;
    } catch (e) {
      console.error('ensureRexyThread failed:', e);
      return null;
    }
  };

  // DAY CHATS (owner ruling 2026-10-16): "End chat" no longer closes the day's chat and no
  // longer creates a "Chat N" — it closes nothing on the server. It takes him to today's chat.
  const handleRexyEndThread = async (_threadId: string) => {
    await handleRexyNewThread();
  };

  const handleRexyRenameThread = async (threadId: string, newName: string) => {
    try {
      await axios.post(`${API_BASE}/threads/rename`, {
        domain: REXY_HOME_DOMAIN, agent: REXY_HOME_AGENT, thread_id: threadId, name: newName,
      }, { withCredentials: true });
      setRexyThreads(prev => prev.map(t => t.id === threadId ? { ...t, name: newName } : t));
    } catch (e) { console.error('Rexy rename thread failed:', e); }
  };

  const handleRexyDeleteThread = async (threadId: string) => {
    try {
      await axios.post(`${API_BASE}/threads/delete`, {
        domain: REXY_HOME_DOMAIN, agent: REXY_HOME_AGENT, thread_id: threadId,
      }, { withCredentials: true });
      setRexyThreads(prev => prev.filter(t => t.id !== threadId));
      if (threadId === rexyActiveThreadId) {
        const remaining = rexyThreads.filter(t => t.id !== threadId && t.status === 'active');
        if (remaining.length > 0) {
          setRexyActiveThreadId(remaining[0].id);
          loadConversationHistory(REXY_HOME_DOMAIN, REXY_HOME_AGENT, remaining[0].id);
        } else {
          handleRexyNewThread();
        }
      }
    } catch (e) { console.error('Rexy delete thread failed:', e); }
  };

  // Honest no-op upload handlers — the home chat hides the attach affordance
  // (uploadsDisabled) and HOMEPAGE uploads are backend-walled; these never write a
  // fake local assistant result (Ghost build-sign guard).
  const rexyNoUpload = async () => { /* intentional no-op — no pseudo-file-path */ };

  const handleRexySend = async (text: string) => {
    // Resolve a STABLE thread key BEFORE any append/send so render-key == send-key == poll-key
    // (turn-binding fix: previously optimistic landed under HOMEPAGE_REXY while the poll wrote
    // HOMEPAGE_REXY_<tid>, orphaning the fresh reply and re-serving prior turns). opt_* ids mark
    // in-flight optimistic messages for the reconciler.
    const tid = await ensureRexyThread();
    const key = tid ? `${REXY_HOME_DOMAIN}_${REXY_HOME_AGENT}_${tid}` : rexyChatKey;
    const userMsg: Message = {
      id: `opt_user_${Date.now()}`, sender: 'user', text,
      timestamp: new Date().toLocaleTimeString(),
    };
    setAllMessages(prev => ({ ...prev, [key]: [...(prev[key] || []), userMsg] }));
    setRexyLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/query`, {
        goal: text,
        domain: REXY_HOME_DOMAIN,
        agent: REXY_HOME_AGENT,
        thread_id: tid,
        model: activeModel,
        routing_mode: routingMode,
        fallback_target: fallbackTarget,
      }, { withCredentials: true });

      let responseText = '';
      let source = 'CLAUDE';
      if (response.data.success) {
        const data = response.data;
        // The 6.3 validated HOMEPAGE/REXY path emits observations=[{content: render}];
        // fall through the other shapes for parity with the nav chat.
        if (data.observations && data.observations.length > 0) {
          responseText = data.observations
            .map((o: any) => o.content || o)
            .filter((o: string) => o && o.trim())
            .join('\n\n');
        } else if (data.memory_response) {
          responseText = String(data.memory_response);
        } else if (data.facts && Object.keys(data.facts).length > 0) {
          responseText = (Object.values(data.facts) as string[]).filter(v => v && v.trim()).join('\n\n');
        } else if (data.insights && data.insights.length > 0) {
          responseText = data.insights.map((i: any) => i.summary || '').filter((s: string) => s.trim()).join('\n\n');
        }
        const tagApproval = responseText.startsWith('[APPROVAL_REQUIRED]');
        const intentApproval = data.intent?.requires_approval === true;
        if (tagApproval || intentApproval) {
          if (tagApproval) responseText = responseText.replace('[APPROVAL_REQUIRED]', '').trim();
          const confirmed = window.confirm('Rexy classified this as a high-risk action.\n\nDo you want to proceed?');
          if (!confirmed) {
            // RECITE-WIRING-DELTA — cancellation is an INTERFACE outcome, not
            // her line. The gated response is withheld; the system reports it.
            setAllMessages(prev => ({
              ...prev,
              [key]: [...(prev[key] || []), systemNotice('Action cancelled — the high-risk response was withheld.', 'status')],
            }));
            return;
          }
        }
        source = data.source || 'CLAUDE';
        if (!responseText.trim()) {
          // Empty successful turn — a state report, never a canned Rexy line.
          setAllMessages(prev => ({
            ...prev,
            [key]: [...(prev[key] || []), systemNotice('Turn completed — no text response.', 'status')],
          }));
          return;
        }
        const rexyMsg: Message = {
          id: `opt_rexy_${Date.now()}`, sender: 'rexy', text: responseText,
          timestamp: new Date().toLocaleTimeString(), source: source as any,
          fallbackNotice: response.data.fallback_notice || undefined,
          ...(response.data.images ? { images: response.data.images } : {}),
        };
        setAllMessages(prev => ({ ...prev, [key]: [...(prev[key] || []), rexyMsg] }));
      } else {
        // RECITE-WIRING-DELTA — typed failure outcome (success:false on 200):
        // {blocked, error_type:'output_blocked', reason} et al. Rendered as a
        // SYSTEM error notice with the backend reason surfaced — never as her.
        const d = response.data || {};
        const reason = d.reason || d.message || d.error;
        const text = d.error_type === 'output_blocked' || d.blocked
          ? `Governance blocked this response${reason ? ` — ${reason}` : '.'}`
          : `Request failed${reason ? ` — ${reason}` : '.'}`;
        setAllMessages(prev => ({ ...prev, [key]: [...(prev[key] || []), systemNotice(text, 'error')] }));
      }
    } catch (e: any) {
      // RECITE-WIRING-DELTA — transport/5xx failures carry typed outcomes
      // (502 {error_type:'infrastructure'|'rexy_turn_error', reason}). The
      // INTERFACE reports them; Rexy never voices a connection error.
      const d = e?.response?.data;
      const reason = d?.reason || d?.error || d?.detail;
      let text = 'Connection error — the Rexy server could not be reached.';
      if (d?.error_type === 'infrastructure') {
        text = `Infrastructure error${reason ? ` — ${reason}` : '.'}`;
      } else if (d?.error_type === 'rexy_turn_error') {
        text = `Rexy's turn failed${reason ? ` — ${reason}` : '.'}`;
      } else if (reason) {
        text = String(reason);
      }
      setAllMessages(prev => ({ ...prev, [key]: [...(prev[key] || []), systemNotice(text, 'error')] }));
    } finally {
      setRexyLoading(false);
    }
  };

  const fetchSecurity = async (level = securityLevel) => {
    try {
      const res = await axios.get(`${API_BASE}/security`, {
        params: { level },
        withCredentials: true,
      });
      setSecurityFlags(res.data.flags || []);
      setSecuritySummary(res.data.summary || {});
    } catch (e) {
      console.error('Security fetch failed:', e);
    }
  };

  const handleMemoryOverrideToggle = async () => {
    try {
      const newState = !memoryOverride;
      await axios.post(`${API_BASE}/memory-override`,
        { active: newState },
        { withCredentials: true }
      );
      setMemoryOverride(newState);
    } catch (e) {
      console.error('Memory override toggle failed:', e);
    }
  };

  const fetchMemory = async (domain = memoryDomain) => {
    try {
      const res = await axios.get(`${API_BASE}/memory`, {
        params: { domain },
        withCredentials: true,
      });
      setMemoryEntries(res.data.entries || []);
      setMemoryDomain(domain);
    } catch (e) {
      console.error('Memory fetch failed:', e);
    }
  };

  const handleQuit = async () => {
    try {
      // Clear session key first
      await axios.post(`${API_BASE}/clear-session`, {}, { withCredentials: true });
    } catch (e) {}
    // Signal Electron to quit
    if ((window as any).electronAPI?.quit) {
      (window as any).electronAPI.quit();
    } else {
      // Fallback — close window
      window.close();
    }
  };

  const handleOpenSecurity = () => {
    setShowSecurityDash(true);
    fetchSecurity(securityLevel);
  };

  // Update model when agent switches
  React.useEffect(() => {
    if (activeAgent) {
      setActiveModel('nvidia/Qwen3.6-35B-A3B-NVFP4');  // All agents default to local
    } else {
      setActiveModel('nvidia/Qwen3.6-35B-A3B-NVFP4');
    }
  }, [activeAgent]);

  // ACTIVATING AN AGENT EITHER HAPPENS OR IS REPORTED — owner ruling 2026-08-24.
  //
  // This catch was the LIVE FAULT behind 63 blocked documents, and it is the same
  // shape as the held-list catch closed earlier today: it kept the app running
  // and told nobody. `setScreen('agent')` at the ONE call site ran regardless, so
  // a failed activation opened Buck's chat with `activeAgent` null — a chat that
  // looks exactly like a working one. Everything downstream then read that null:
  // the held-list effect short-circuited on its own guard (probe:
  // `called: false, why: 'no active agent'`), and an upload would have sent
  // `agent: ''`, which the door reads as the DOMAIN'S doctrine rather than the
  // agent's.
  //
  // PROVEN BY ELIMINATION, not by inference: `setScreen('agent')` has exactly one
  // call site in this file, and `ceo.set_agent` stores whatever it is handed, so
  // a null `activeAgent` on the chat screen can only mean this request failed.
  //
  // It now RETURNS WHETHER IT SUCCEEDED, so the caller can decline to navigate,
  // and it never throws — the promise the old catch was keeping is kept.
  const handleAgentChange = async (agent: string | null): Promise<boolean> => {
    try {
      await axios.post(
        `${API_BASE}/agent`,
        { agent },
        { withCredentials: true }
      );
      (window as any).__rexyAgentProbe = { attempted: agent, ok: true, at: new Date().toISOString() };
      setAgentError(null);
      setActiveAgent(agent);
      setActiveThreadId(null);
      setThreads([]);
      // Clear all messages for previous agent keys — prevents stale state
      // from previous agent appearing before new thread loads
      if (activeAgent) {
        setAllMessages(prev => {
          const cleared = { ...prev };
          Object.keys(cleared).forEach(k => {
            if (k.startsWith(`${activeDomain}_${activeAgent}`)) {
              delete cleared[k];
            }
          });
          return cleared;
        });
      }
      if (agent) {
        // Load threads for new agent — this also loads conversation history
        await loadThreads(activeDomain, agent);
      }
      return true;
    } catch (error: any) {
      // NOT SILENT, AND STILL NOT FATAL. It writes no message state and touches
      // no other screen — it sets one string that renders where the consequence
      // lands, and reports false so the caller does not open a chat for an agent
      // that was never activated.
      const status = error?.response?.status;
      const why = error?.response?.data?.error || error?.message || 'no reason returned';
      (window as any).__rexyAgentProbe = {
        attempted: agent, ok: false, status: status || 'none', error: why,
        at: new Date().toISOString(),
      };
      console.error('Agent change failed:', error);
      if (agent) {
        setAgentError(
          `${agent} was not activated` + (status ? ` (${status})` : '') + ` — ${why}.`
          + ` Its chat was not opened, because a chat with no active agent files`
          + ` uploads as the domain's doctrine instead of the agent's.`);
      }
      return false;
    }
  };

  const handleOverrideToggle = async (active: boolean, apiName?: string) => {
    try {
      // F1 Build B — the UI routing role of /api/override is retired (routing now
      // rides `routing_mode` on /api/query). This handler is kept only as a
      // defensive backstop; if any caller still passes a bare provider label like
      // 'CLAUDE' it MUST be mapped to a real model id — the backend `model` field
      // is a concrete model, never a provider string.
      const realModel = apiName === 'CLAUDE' ? 'claude-opus-4-7' : (apiName || null);
      await axios.post(
        `${API_BASE}/override`,
        { active, api: apiName || null, model: realModel },
        { withCredentials: true }
      );
      setOverrideMode(active);
      setOverrideApi(active && apiName ? apiName : null);
      setShowOverrideMenu(false);
    } catch (error) {
      console.error('Override toggle failed:', error);
    }
  };

  // WHICH MODEL ANSWERED — mapped from the backend's own route, never assumed.
  // brain_bridge.get_last_route() is the authority; the upload response carries
  // it as `answered_by`. Before this, both upload handlers hardcoded 'CLAUDE',
  // so a reply produced on the box by Qwen was badged to the owner as a
  // frontier-API answer — on the one path where that distinction matters most.
  // An unknown route is left blank rather than guessed: no badge is better than
  // a wrong one, which is the whole lesson here.
  const routeLabel = (route?: string | null): string | undefined => {
    switch (route) {
      case 'local_qwen':  return 'LOCAL_AI';
      case 'api_claude':  return 'CLAUDE_API';
      case 'api_openai':  return 'GPT-4';
      case 'sub_claude':  return 'SUB_CLAUDE';
      case 'sub_gpt':     return 'SUB_GPT';
      default:            return undefined;
    }
  };

  // ── A HELD DOCUMENT, AND THE ONE TAP THAT FILES IT ────────────────────────
  //
  // Owner correction 2026-08-17: an unclear folder is a QUESTION, not a
  // rejection. The backend holds the document whole and asks in the agent's
  // chat; this is the answer half. THE OWNER NEVER UPLOADS THE SAME THING
  // TWICE — the document is already on the box, and this only says where.
  const [pendingHold, setPendingHold] = useState<any>(null);

  // WHAT THE HELD-LIST DOOR LAST DID — owner ruling 2026-08-24, item 4.
  //
  // The catch below was silent on purpose ("a held-list failure must never
  // break the chat") and that half is right. But SILENT is not the same as
  // SAFE: while GET /api/upload/held 400'd on the OS node id, the ask did not
  // render AT ALL, and an absent affordance reads exactly like a document that
  // was never taken. Three rounds were spent on a backend that was working,
  // because the fault ate the affordance before it reached the answer.
  //
  // So the failure is now KEPT and SHOWN — in the ask's own place, never in the
  // chat, so nothing it does can break a conversation.
  const [heldError, setHeldError] = useState<string | null>(null);

  // WHY THE AGENT IS NOT ACTIVE, when it was asked to be. Declared here beside
  // the held-list error because it is the same class one step upstream, and
  // because a null `activeAgent` is the CAUSE the held-list strip can only
  // report as a consequence.
  const [agentError, setAgentError] = useState<string | null>(null);

  // THE FREE-TEXT ANSWER — owner ruling 2026-08-24, Forge's contract.
  // `folder` is FREE TEXT: it need not exist and nothing validates it against
  // the offer, because THE UPLOAD IS THE DECLARATION — write_doctrine declares
  // the folder before placing it. So the chooser's chips are a convenience, and
  // this input is the mechanism. Held per hold, cleared when the hold changes.
  const [folderDraft, setFolderDraft] = useState('');
  const [filing, setFiling] = useState(false);
  const [placementError, setPlacementError] = useState<string | null>(null);
  // DISCARD IS TWO TAPS — owner ruling 2026-09-23. The hold is the only copy
  // of what was uploaded, so the first tap arms and the second deletes.
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // WHICH HELD-LIST REQUEST IS THE LIVE ONE. Replaces the per-effect `cancelled`
  // flag now that the fetch has TWO callers (agent open, and a successful
  // filing): a sequence the newest caller owns is the only thing that keeps a
  // slow earlier response from overwriting a newer one, and it works across
  // callers where a closure flag does not.
  const heldReqRef = useRef(0);

  // The question survives a reload, because the hold does. A held document the
  // owner cannot see again is the loss this path exists to prevent, arriving
  // quietly instead of loudly.
  const refreshHeld = useCallback(async (domain: string, agent: string | null) => {
    // The token is read inline from window rather than the `authToken` const,
    // which is declared further down this component — the same convention the
    // file already notes at its other pre-declaration read site.
    if (!agent || !(window as any).__rexyToken) {
      heldReqRef.current += 1;
      setPendingHold(null);
      setHeldError(null);
      // NOT ASKED IS NOT THE SAME AS ASKED-AND-EMPTY. The probe says which.
      (window as any).__rexyHeldProbe = {
        called: false,
        why: !agent ? 'no active agent' : 'no token',
        at: new Date().toISOString(),
      };
      return;
    }
    // WHAT WAS SENT, recorded BEFORE the call — so a probe read after a failure
    // still names the agent value the door was asked about. Which shape this is
    // (`buck` from the sidebar, `os:agent:growth-buck` from Mission Control) is
    // the open question reported to the manager this session, NOT changed here.
    const seq = ++heldReqRef.current;
    const asked = { domain, agent };
    (window as any).__rexyHeldProbe = { called: true, sent: asked, at: new Date().toISOString() };
    try {
      const res = await axios.get(`${API_BASE}/upload/held`, {
        params: { domain, agent },
      });
      if (heldReqRef.current !== seq) return;   // superseded by a newer ask
      const list = res.data?.held || [];
      const first = list[0];
      // COUNTS AND THE ANSWERED SHAPE ONLY — never a document's contents.
      (window as any).__rexyHeldProbe = {
        called: true, sent: asked, status: res.status,
        held: list.length, offer: (res.data?.offer || []).length,
        at: new Date().toISOString(),
      };
      setHeldError(null);
      setPendingHold(first
        ? { ...first, offer: res.data?.offer || [], domain, agent }
        : null);
    } catch (err: any) {
      // A HELD-LIST FAILURE MUST NEVER BREAK THE CHAT — AND MUST NEVER BE
      // SILENT. It touches no message state and no other screen; it sets one
      // string that renders as a strip where the ask would have been.
      if (heldReqRef.current !== seq) return;
      const status = err?.response?.status;
      const why = err?.response?.data?.error || err?.message || 'no reason returned';
      (window as any).__rexyHeldProbe = {
        called: true, sent: asked, status: status || 'none', error: why,
        at: new Date().toISOString(),
      };
      setHeldError(
        `Could not read what ${asked.agent} is holding`
        + (status ? ` (${status})` : '') + ` — ${why}.`
        + ` Any held document is still on the box; this is the list, not the document.`);
    }
  }, []);

  useEffect(() => { refreshHeld(activeDomain, activeAgent); }, [activeDomain, activeAgent, refreshHeld]);

  // A NEW HOLD IS A NEW QUESTION — the draft and the last failure belong to the
  // hold that produced them, never to the one that follows it.
  useEffect(() => {
    setFolderDraft('');
    setPlacementError(null);
    setConfirmDiscard(false);
  }, [pendingHold?.hold_id]);

  // THE WAY OUT BESIDE THE QUESTION — owner ruling 2026-09-23. A held document
  // that is not going anywhere (another agent's manual, a mistaken drop) could
  // only be cleared over SSH. POST /upload/held/discard deletes the hold and
  // writes nothing to T1; `already_gone` means it was filed or discarded
  // elsewhere, which is the same end state, so the list is re-read either way.
  const discardHeld = async () => {
    if (!pendingHold || filing) return;
    const key = `${pendingHold.domain}_${pendingHold.agent}`;
    const answeredFor = { domain: pendingHold.domain, agent: pendingHold.agent };
    const name = pendingHold.filename || 'the held document';
    setFiling(true);
    setPlacementError(null);
    try {
      await axios.post(`${API_BASE}/upload/held/discard`, {
        domain: pendingHold.domain,
        agent:  pendingHold.agent,
        hold_id: pendingHold.hold_id,
        reason: 'discarded by the owner from the chat',
      });
    } catch (err: any) {
      if (!err?.response?.data?.already_gone) {
        setPlacementError(err?.response?.data?.error || err?.message || 'no reason returned');
        setConfirmDiscard(false);
        setFiling(false);
        return;
      }
    }
    setAllMessages(prev => ({
      ...prev,
      [key]: [...(prev[key] || []), {
        ...systemNotice(`Discarded ${name} — nothing was filed.`, 'status'),
        id: `placement_discard_${Date.now()}`,
      }],
    }));
    setPendingHold(null);
    setFiling(false);
    refreshHeld(answeredFor.domain, answeredFor.agent);
  };

  // `folder` REACHES THE DOOR EXACTLY AS TYPED — owner ruling 2026-08-24. It is
  // not trimmed, not lower-cased, not split on " / " and not checked against the
  // offer. The door reads " / "-separated segments as a PATH and validates and
  // normalises EACH SEGMENT ITSELF, keeping the owner's spelling per segment; a
  // front end that normalised first would be answering a question the door has
  // already answered, with less information than the door has. The chips pass
  // their own `t.folder` through this same parameter — one path, not two.
  const answerPlacement = async (folder: string, slot: string | null) => {
    if (!pendingHold || filing) return;   // one answer in flight per hold
    const key = `${pendingHold.domain}_${pendingHold.agent}`;
    const answeredFor = { domain: pendingHold.domain, agent: pendingHold.agent };
    setFiling(true);
    setPlacementError(null);
    try {
      const res = await axios.post(`${API_BASE}/upload/placement`, {
        domain: pendingHold.domain,
        agent:  pendingHold.agent,
        hold_id: pendingHold.hold_id,
        folder,
        slot: slot || '',
      });
      const d = res.data;
      setAllMessages(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), {
          ...systemNotice(
            d.success
              ? `Filed as doctrine: ${d.doctrine_agent} · T1 · ${d.doctrine_folder}`
                + (d.doctrine_filled_slot ? ' (filled the waiting slot)' : '')
              : `Still held — ${d.error}`,
            d.success ? 'status' : 'error'),
          id: `placement_answer_${Date.now()}`,
        }],
      }));
      // `still_held` IS THE DOOR'S OWN WORD FOR IT, and it decides the bar.
      // Success alone does not take the bar down: if the door says the document
      // is still held then it is, and the affordance to answer again must
      // survive — that is the whole reason the field is explicit.
      if (d.success && d.still_held !== true) {
        setPendingHold(null);
        setFolderDraft('');
        // FOUR ARE HELD, AND 63 ARE BEHIND THEM. Clearing the bar on the one
        // just filed would hide the next, and the effect above cannot re-fire —
        // its deps did not change. So the list is re-read here: the next held
        // document surfaces in the same breath, and the queue drains without a
        // reload. Nothing surfaces if the list is now empty.
        refreshHeld(answeredFor.domain, answeredFor.agent);
      } else {
        setPlacementError(d.error || 'The door refused the answer and gave no reason.');
      }
    } catch (err: any) {
      const why = err?.response?.data?.error || err?.message || 'no reason returned';
      setAllMessages(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), {
          ...systemNotice(
            `Still held — ${why}. The document is safe; answer again.`, 'error'),
          id: `placement_answer_${Date.now()}`,
        }],
      }));
      // THE BAR STAYS UP ON EVERY FAILURE PATH. A refused answer that also took
      // away the way to answer would be the silence this whole round removed,
      // arriving one layer in.
      setPlacementError(why);
    } finally {
      setFiling(false);
    }
  };

  // THE DOOR'S PLACEMENT ANSWER, SURFACED — owner ruling 2026-08-17, item 2.
  //
  // ONE builder for BOTH upload paths. The ask is the whole point of the
  // ruling: when a document does not declare a folder the door refuses to
  // guess, and an ask nobody can see is the same as a silent default. It is a
  // SYSTEM notice, never an agent message — the door said this, not Rexy.
  const placementNotice = (result: any): Message | null => {
    const id = `placement_${Date.now()}`;
    if (result?.doctrine_record) {
      const where = result.doctrine_agent || `${result.memory_domain} (domain)`;
      const folder = result.doctrine_folder_declared || result.doctrine_folder;
      return { ...systemNotice(`Filed as doctrine: ${where} · T1 · ${folder}`, 'status'), id };
    }
    // HELD, NOT LOST. The document is on the box, whole, waiting on one answer
    // — so this reads as a question, not as a failure.
    if (result?.held) {
      return { ...systemNotice(
        `Holding ${result.held.question ? '' : 'this document'}${result.held.question || ''}`,
        'status'), id };
    }
    const ask = result?.placement_required;
    // ANOTHER AGENT'S DOCUMENT — the door refused it by name and held nothing
    // (owner ruling 2026-09-23). Its sentence is the whole answer.
    if (ask?.belongs_to) {
      return { ...systemNotice(ask.reason, 'error'), id };
    }
    if (ask) {
      return { ...systemNotice(
        `NOT filed as doctrine — ${ask.reason}. ${ask.needs}`, 'error'), id };
    }
    if (result?.doctrine_refused) {
      return { ...systemNotice(`Not filed as doctrine — ${result.doctrine_refused}`, 'status'), id };
    }
    return null;
  };

  const handleFileUpload = async (file: File) => {
    const domain = activeDomain;
    const agent = activeAgent || '';
    const key = `${domain}_${agent}`;

    // ── AN UPLOAD WITH NO ACTIVE AGENT REFUSES ────────────────────────────
    // Owner ruling 2026-08-24. `agent: ''` is not an absence the door can read
    // safely: it means THE DOMAIN'S OWN DOCTRINE, so a manual meant for BUCK's
    // T1 files at GROWTH level instead. AND IT CANNOT BE PUT RIGHT — the system
    // has no path to rewrite T1. Refusing costs one upload; sending empty costs
    // the doctrine, permanently.
    //
    // NOTHING REAL IS LOST BY REFUSING. Every upload affordance in this app sits
    // inside an agent's chat (AgentViewScreen); the homepage is wired to
    // `rexyNoUpload` with `uploadsDisabled`. So there is no surface on which the
    // owner deliberately files at domain level — a null agent here is ALWAYS the
    // fault above, never an intention. If a deliberate domain-level door is ever
    // wanted it is a new affordance that SAYS so, not this silence.
    if (!agent) {
      setAllMessages(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), {
          ...systemNotice(
            `${file.name} was NOT uploaded — no agent is active, and a document`
            + ` filed without one becomes the domain's doctrine instead of the`
            + ` agent's, which cannot be undone. Reopen the agent's chat and try`
            + ` again. Nothing was sent and nothing was stored.`, 'error'),
          id: `upload_refused_${Date.now()}`,
        }],
      }));
      return;
    }

    // RECITE-WIRING-DELTA — upload progress is INTERFACE state, not Rexy.
    const uploadingMsg: Message = {
      ...systemNotice(`Processing file: ${file.name}…`, 'status'),
      id: `upload_${Date.now()}`,
    };
    setAllMessages(prev => ({ ...prev, [key]: [...(prev[key] || []), uploadingMsg] }));

    try {
      let fileData: string;
      let fileExt: string;
      let fileName: string;

      // Check if from Electron native dialog
      if ((file as any)._electronData) {
        const ed = (file as any)._electronData;
        fileData = ed.data;
        fileExt = ed.ext;
        fileName = ed.name;
      } else {
        // Browser file input — convert to base64
        fileName = file.name;
        fileExt = '.' + file.name.split('.').pop();
        fileData = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]); // Remove data:... prefix
          };
          reader.readAsDataURL(file);
        });
      }

      const rexyToken = window.__rexyToken || '';
      const res = await axios.post(`${API_BASE}/upload`, {
        data: fileData,
        name: fileName,
        ext: fileExt,
        domain: domain,
        // THE CHAT KNOWS WHICH AGENT IT IS — SEND IT (owner ruling
        // 2026-08-17, item 1). An attachment in an agent's chat belongs in
        // that agent's T1, and the door cannot place it there if the surface
        // never says whose chat it was. Mobile has always sent this
        // (mobile/rexy-ios/src/api/chat.ts); localhost and Electron dropped
        // it, so every upload from a desktop arrived anonymous and fell back
        // to whatever the server happened to have active. Empty string means
        // no agent is active, which the door reads as the DOMAIN's own
        // doctrine — an absence, not a default.
        agent: agent,
        // 'have the agent respond' — renamed from send_to_claude, which
        // described a behaviour it does not have (owner ruling 2026-08-17).
        agent_reply: true,
      }, {
        withCredentials: true,
        headers: { 'X-Rexy-Token': rexyToken },
        timeout: laneTimeout(domain, agent, 180000), // 3 minutes — large PDF + Claude processing; none on a Sub lane (R29)
        maxContentLength: 50 * 1024 * 1024,  // 50MB — handles large PDFs as base64
        maxBodyLength: 50 * 1024 * 1024,     // 50MB — axios body limit
      });

      const result = res.data;

      // RECITE-WIRING-DELTA — split the old single Rexy-attributed result:
      // processing metadata is a SYSTEM status notice; only the model-produced
      // extraction (structured_content/preview, genuine CLAUDE output) renders
      // as an agent message. Failure is a SYSTEM error with the reason.
      // TWO WRITES, SAID AS TWO. The flat silo copy and the T1 doctrine record
      // are different destinations, and " Saved to TRADING memory." beside
      // "Filed as doctrine: ARTHUR · T1 · Live Session" read as one write
      // described twice — or worse, as the wrong destination.
      const memorySaved = result.memory_saved
        ? ` Copy saved to ${result.memory_domain} searchable memory.` : '';
      const outcome: Message[] = [];
      if (result.success) {
        outcome.push({
          ...systemNotice(`File processed: ${fileName} — ${result.word_count} words extracted.${memorySaved}`, 'status'),
          id: `upload_result_${Date.now()}`,
        });
        const placement = placementNotice(result);
        if (placement) outcome.push(placement);
        // The chooser appears the moment the agent asks — same tap, same turn.
        if (result.held) setPendingHold({ ...result.held, domain, agent });
        const extracted = String(result.structured_content || result.preview || '').trim();
        if (extracted) {
          outcome.push({
            id: `upload_content_${Date.now()}`,
            sender: 'rexy',
            text: extracted,
            timestamp: new Date().toLocaleTimeString(),
            // WHICH MODEL ANSWERED, from the backend's route — not a
            // hardcoded badge. A locally-produced Qwen reply was being
            // shown to the owner as a frontier-API answer.
            source: routeLabel(result.answered_by),
            ...(result.images ? { images: result.images } : {}),
          });
        }
      } else {
        outcome.push({
          ...systemNotice(`Upload failed — ${result.error || 'no reason returned'}`, 'error'),
          id: `upload_result_${Date.now()}`,
        });
      }

      setAllMessages(prev => {
        const msgs = prev[key] || [];
        return {
          ...prev,
          [key]: [...msgs.filter(m => m.id !== uploadingMsg.id), ...outcome]
        };
      });

    } catch (err: any) {
      const errDetail = err?.response?.data?.error || err?.message || 'Unknown error';
      console.error('[UPLOAD] Failed:', errDetail, err);
      const errMsg: Message = {
        ...systemNotice(`Upload error — ${errDetail}`, 'error'),
        id: `upload_err_${Date.now()}`,
      };
      setAllMessages(prev => ({
        ...prev,
        [key]: [...(prev[key] || []).filter(m => m.id !== uploadingMsg.id), errMsg]
      }));
    }
  };

  const handleSendWithFile = async (text: string, file: File) => {
    const domain = activeDomain;
    const agent = activeAgent || '';
    const key = chatKey;

    // ── AN UPLOAD WITH NO ACTIVE AGENT REFUSES ────────────────────────────
    // Owner ruling 2026-08-24. `agent: ''` is not an absence the door can read
    // safely: it means THE DOMAIN'S OWN DOCTRINE, so a manual meant for BUCK's
    // T1 files at GROWTH level instead. AND IT CANNOT BE PUT RIGHT — the system
    // has no path to rewrite T1. Refusing costs one upload; sending empty costs
    // the doctrine, permanently.
    //
    // NOTHING REAL IS LOST BY REFUSING. Every upload affordance in this app sits
    // inside an agent's chat (AgentViewScreen); the homepage is wired to
    // `rexyNoUpload` with `uploadsDisabled`. So there is no surface on which the
    // owner deliberately files at domain level — a null agent here is ALWAYS the
    // fault above, never an intention. If a deliberate domain-level door is ever
    // wanted it is a new affordance that SAYS so, not this silence.
    if (!agent) {
      setAllMessages(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), {
          ...systemNotice(
            `${file.name} was NOT uploaded — no agent is active, and a document`
            + ` filed without one becomes the domain's doctrine instead of the`
            + ` agent's, which cannot be undone. Reopen the agent's chat and try`
            + ` again. Nothing was sent and nothing was stored.`, 'error'),
          id: `upload_refused_${Date.now()}`,
        }],
      }));
      return;
    }


    // Show user message with file attachment indicator
    const userMsg: Message = {
      id: Date.now().toString(),
      sender: 'user',
      text: text ? `📎 ${file.name}\n\n${text}` : `📎 ${file.name}`,
      timestamp: new Date().toLocaleTimeString(),
    };
    setAllMessages(prev => ({ ...prev, [key]: [...(prev[key] || []), userMsg] }));

    // RECITE-WIRING-DELTA — processing indicator is INTERFACE state, not Rexy.
    const processingMsg: Message = {
      ...systemNotice(`Processing file: ${file.name}…`, 'status'),
      id: `upload_${Date.now()}`,
    };
    setAllMessages(prev => ({ ...prev, [key]: [...(prev[key] || []), processingMsg] }));
    setIsLoading(true);

    try {
      let fileData: string;
      let fileExt: string;
      let fileName: string;

      if ((file as any)._electronData) {
        const ed = (file as any)._electronData;
        fileData = ed.data;
        fileExt = ed.ext;
        fileName = ed.name;
      } else {
        fileName = file.name;
        fileExt = '.' + file.name.split('.').pop();
        fileData = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          };
          reader.readAsDataURL(file);
        });
      }

      const rexyToken = window.__rexyToken || '';
      const res = await axios.post(`${API_BASE}/upload`, {
        data: fileData,
        name: fileName,
        ext: fileExt,
        domain: domain,
        // Same ruling as handleFileUpload — the send-with-message path is the
        // one an attachment actually arrives through, so it is the one that
        // most needed to say whose chat it was.
        agent: agent,
        context_message: text || '',  // User's message sent alongside file
        // 'have the agent respond' — renamed from send_to_claude, which
        // described a behaviour it does not have (owner ruling 2026-08-17).
        agent_reply: true,
      }, {
        withCredentials: true,
        headers: { 'X-Rexy-Token': rexyToken },
        timeout: laneTimeout(domain, agent, 180000), // 3 minutes — large PDF + Claude processing; none on a Sub lane (R29)
        maxContentLength: 50 * 1024 * 1024,  // 50MB
        maxBodyLength: 50 * 1024 * 1024,     // 50MB
      });

      const result = res.data;

      // RECITE-WIRING-DELTA — same split as handleFileUpload: metadata is a
      // SYSTEM status notice; only genuine model extraction renders as agent.
      const memorySaved = result.memory_saved
        ? ` Copy saved to ${result.memory_domain} searchable memory.`
        : '';
      const contextNote = text
        ? ` Context provided: "${text}"`
        : '';
      const outcome: Message[] = [];
      if (result.success) {
        outcome.push({
          ...systemNotice(`File processed: ${fileName} — ${result.word_count} words extracted.${memorySaved}${contextNote}`, 'status'),
          id: `upload_result_${Date.now()}`,
        });
        const placement = placementNotice(result);
        if (placement) outcome.push(placement);
        // The chooser appears the moment the agent asks — same tap, same turn.
        if (result.held) setPendingHold({ ...result.held, domain, agent });
        const extracted = String(result.structured_content || result.preview || '').trim();
        if (extracted) {
          outcome.push({
            id: `upload_content_${Date.now()}`,
            sender: 'rexy',
            text: extracted,
            timestamp: new Date().toLocaleTimeString(),
            // WHICH MODEL ANSWERED, from the backend's route — not a
            // hardcoded badge. A locally-produced Qwen reply was being
            // shown to the owner as a frontier-API answer.
            source: routeLabel(result.answered_by),
            ...(result.images ? { images: result.images } : {}),
          });
        }
      } else {
        outcome.push({
          ...systemNotice(`Upload failed — ${result.error || 'no reason returned'}`, 'error'),
          id: `upload_result_${Date.now()}`,
        });
      }

      setAllMessages(prev => {
        const msgs = prev[key] || [];
        return {
          ...prev,
          [key]: [...msgs.filter(m => m.id !== processingMsg.id), ...outcome]
        };
      });

    } catch (err: any) {
      const errDetail = err?.response?.data?.error || err?.message || 'Unknown error';
      console.error('[UPLOAD] SendWithFile failed:', errDetail, err);
      const errMsg: Message = {
        ...systemNotice(`Upload error — ${errDetail}`, 'error'),
        id: `upload_err_${Date.now()}`,
      };
      setAllMessages(prev => ({
        ...prev,
        [key]: [...(prev[key] || []).filter(m => m.id !== processingMsg.id), errMsg]
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Multi-file handler — sends files sequentially, message attached to first file only
  const handleSendWithFiles = async (text: string, files: File[]) => {
    for (let i = 0; i < files.length; i++) {
      await handleSendWithFile(i === 0 ? text : '', files[i]);
    }
  };

  const handleSendMessage = async (text: string) => {
    // Stage F gate: /api/query now hard-rejects missing/empty domain+agent
    // via validate_domain_agent. Mirror the pollMessages guard at line 233 —
    // no send fires until both are populated, which is also the contract the
    // UI implies (the chat surface is only mounted after an agent is picked).
    if (!activeDomain || !activeAgent) return;
    // R12 (B-2e5): her turn goes into today's chat and is drawn there (bucklyTurnChat). This chatKey is the
    // chat the turn goes into; for every other agent it is the page's own, worked out as chatKey is above.
    const bucklyToday = isBucklyChat() ? await bucklyTurnChat() : null;
    const turnThread = bucklyToday || activeThreadId;
    const chatKey = turnThread
      ? `${activeDomain}_${activeAgent || 'CEO'}_${turnThread}`
      : `${activeDomain}_${activeAgent || 'CEO'}`;

    const mackRequestId = isMackTextTurn(activeDomain, activeAgent) ? newMackRequestId() : null;
    const userMsg: Message = {
      id: Date.now().toString(),
      sender: 'user',
      text,
      timestamp: new Date().toLocaleTimeString(),
      ...(mackRequestId ? { mackTurn: {
        domain: 'TRADING' as const, agent: 'MACK' as const,
        request_id: mackRequestId, current_turn_response: false,
      } } : {}),
    };
    setAllMessages((prev) => ({
      ...prev,
      [chatKey]: [...(prev[chatKey] || []), userMsg],
    }));
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE}/query`,
        {
          goal: text,
          domain: activeDomain,
          agent: activeAgent,
          thread_id: turnThread,
          model: activeModel,
          routing_mode: routingMode,
          fallback_target: fallbackTarget,
          ...(mackRequestId ? { request_id: mackRequestId } : {}),
          ...(activeDomain === 'TRADING' && activeAgent?.toLowerCase() === 'arthur'
            ? { active_surface: arthurActiveSurface, arthur_session_controls: true }
            : {}),
        },
        {
          withCredentials: true,
        }
      );

      if (response.data.success) {
        const data = response.data;
        if (activeDomain === 'TRADING' && activeAgent.toLowerCase() === 'arthur') {
          axios.get(`${API_BASE}/arthur/dashboard`, { params: { thread_id: activeThreadId } })
            .then(res => { if (res.data?.data?.selectedContext) setArthurSessionContext(res.data.data.selectedContext); })
            .catch(() => {});
        }
        if (mackRequestId) {
          const current = mackCurrentResponse(data, mackRequestId);
          const message: Message = current || {
            ...systemNotice('Mack response withheld: this response could not be verified for the current request.', 'error'),
            mackTurn: { domain: 'TRADING', agent: 'MACK', request_id: mackRequestId, current_turn_response: false },
          };
          setLastMemoryStatus(null);
          setAllMessages(prev => ({ ...prev, [chatKey]: [...(prev[chatKey] || []), message] }));
          return;
        }
        let responseText = '';

        // Memory-answered queries — clean up terminal formatting for display
        if (data.from_memory && data.memory_response) {
          const raw = data.memory_response as string;
          // Extract just FACT and OBS content, strip terminal decorators
          const lines = raw.split('\n');
          const cleanLines: string[] = [];
          for (const line of lines) {
            const trimmed = line.trim();
            // Skip header lines and separators
            if (trimmed.startsWith('[MEMORY]')) continue;
            if (trimmed.startsWith('[HIGH') || trimmed.startsWith('[MEDIUM')) continue;
            if (trimmed.startsWith('──') || trimmed.startsWith('==')) continue;
            if (trimmed === '') continue;
            // Clean FACT: and OBS: prefixes
            if (trimmed.startsWith('FACT:')) {
              cleanLines.push(trimmed.replace(/^FACT:\s*/, ''));
            } else if (trimmed.startsWith('OBS:')) {
              cleanLines.push('• ' + trimmed.replace(/^OBS:\s*/, ''));
            } else {
              cleanLines.push(trimmed);
            }
          }
          responseText = cleanLines.join('\n');
          // Also strip system prompt markers if present
          const sysMarkers2 = [
            'RELEVANT KNOWLEDGE FROM MEMORY',
            'END MEMORY CONTEXT',
            'Use the above knowledge silently',
            'Do NOT repeat, quote, or echo it back',
            'Simply answer the user',
          ];
          let lastEnd2 = -1;
          for (const marker of sysMarkers2) {
            const mi = responseText.lastIndexOf(marker);
            if (mi !== -1) {
              const le = responseText.indexOf('\n', mi + marker.length);
              lastEnd2 = Math.max(lastEnd2, le !== -1 ? le : mi + marker.length);
            }
          }
          if (lastEnd2 > 0) responseText = responseText.substring(lastEnd2).trim();
        } else if (data.observations && data.observations.length > 0) {
          // Free-form entry — show observation content only
          let rawObs = data.observations
            .map((o: any) => o.content || o)
            .filter((o: string) => o && o.trim())
            .join('\n\n');

          // Strip system prompt markers if Qwen echoed them back
          const sysMarkers = [
            'RELEVANT KNOWLEDGE FROM MEMORY',
            'END MEMORY CONTEXT',
            'Use the above knowledge silently',
            'Do NOT repeat, quote, or echo it back',
            'Simply answer the user',
          ];
          let lastMarkerEnd = -1;
          for (const marker of sysMarkers) {
            const markerIdx = rawObs.lastIndexOf(marker);
            if (markerIdx !== -1) {
              const lineEnd = rawObs.indexOf('\n', markerIdx + marker.length);
              lastMarkerEnd = Math.max(lastMarkerEnd, lineEnd !== -1 ? lineEnd : markerIdx + marker.length);
            }
          }
          if (lastMarkerEnd > 0) {
            rawObs = rawObs.substring(lastMarkerEnd).trim();
          }
          responseText = rawObs;
        } else if (data.facts && Object.keys(data.facts).length > 0) {
          // Structured entry — show facts only, not insights
          const factValues = Object.values(data.facts) as string[];
          responseText = factValues
            .filter((v: string) => v && v.trim())
            .join('\n\n');
        } else if (data.insights && data.insights.length > 0) {
          responseText = data.insights
            .map((i: any) => i.summary || '')
            .filter((s: string) => s.trim())
            .join('\n\n');
        }

        // MEDIA's conversation likewise: no approval box anywhere in MEDIA's chat (owner ruling 29 Oct).
        // Arthur's conversation is delivered without action approval. Operational
        // permissions remain at the backend's tool and write boundaries.
        if (!(activeDomain === 'TRADING' && activeAgent.toUpperCase() === 'ARTHUR') && activeDomain !== 'MEDIA') {
          const tagApproval = responseText.startsWith('[APPROVAL_REQUIRED]');
          const intentApproval = data.intent?.requires_approval === true;
          if (tagApproval || intentApproval) {
            if (tagApproval) responseText = responseText.replace('[APPROVAL_REQUIRED]', '').trim();
            const confirmed = window.confirm(
              'Rexy classified this as a high-risk action.\n\nDo you want to proceed?'
            );
            if (!confirmed) {
              // RECITE-WIRING-DELTA — cancellation is an INTERFACE outcome, not
              // the agent's line. The gated response is withheld.
              setAllMessages((prev) => ({
                ...prev,
                [chatKey]: [...(prev[chatKey] || []), systemNotice('Action cancelled — the high-risk response was withheld.', 'status')],
              }));
              return;
            }
          }
        }

        // Determine memory status for dot badge
        const memStatus = governedMemoryStatusFromResponse(data, memoryOverride);
        setLastMemoryStatus(memStatus);

        // Determine source badge — MEMORY is no longer standalone
        // Qwen always answers, with or without memory context
        const msgSource = data.source || 'LOCAL_AI';

        if (!responseText.trim()) {
          // RECITE-WIRING-DELTA — empty successful turn: the INTERFACE reports
          // the state; no canned line is attributed to the agent.
          setAllMessages((prev) => ({
            ...prev,
            [chatKey]: [...(prev[chatKey] || []), systemNotice('Turn completed — memory updated, no text response.', 'status')],
          }));
        } else {
          const rexyMsg: Message = {
            id: (Date.now() + 1).toString(),
            sender: 'rexy',
            text: stripMarkdown(responseText.trim()),
            timestamp: new Date().toLocaleTimeString(),
            source: msgSource,
            memoryStatus: memStatus,
            fallbackNotice: data.fallback_notice || undefined,
            ...(data.images ? { images: data.images } : {}),
          };
          // The 10-second poll can land between the server saving this reply and this
          // handler, and add its own copy (restored_<ms>_<i>: no source, no memory status).
          // Swap that copy for this reply instead of showing it twice — only a copy the
          // poll added since this send began, so an earlier identical answer is kept.
          const sentAt = Number(userMsg.id);
          const pollCopyOfThisReply = (m: Message) => {
            const added = /^restored_(\d+)_/.exec(m.id);
            return !!added && Number(added[1]) >= sentAt && m.sender === rexyMsg.sender
              && stripMarkdown(String(m.text).trim()).slice(0, 50) === rexyMsg.text.slice(0, 50);
          };
          setAllMessages((prev) => {
            const list = prev[chatKey] || [];
            const at = list.findIndex(pollCopyOfThisReply);
            return {
              ...prev,
              [chatKey]: at < 0 ? [...list, rexyMsg] : [...list.slice(0, at), rexyMsg, ...list.slice(at + 1)],
            };
          });
        }

        // ── REXY REPORT REMINDER (Step 3 Piece 2, §5C) ─────────────────────
        // Report reminders carry an explicit target envelope. A missing or
        // mismatched scope is denied here, so a report cannot fall into the
        // currently active agent transcript by default.
        if (data.report_reminder && data.report_reminder.reminder) {
          const reportEvent = Array.isArray(data.agent_events)
            ? data.agent_events.find((event: AgentEventEnvelope) => scopeNorm(event?.event_type) === 'REPORT_WAITING')
            : null;
          if (agentEventAllowed(reportEvent, activeEventScopeRef.current)) {
            const remindMsg: Message = {
              id: (Date.now() + 20).toString(),
              sender: 'rexy',
              text: String(data.report_reminder.reminder),
              timestamp: new Date().toLocaleTimeString(),
              source: 'REXY_REPORT',
            };
            setAllMessages((prev) => ({
              ...prev,
              [chatKey]: [...(prev[chatKey] || []), remindMsg,
                systemNotice('Report waiting — LISTEN or SET ASIDE in the Reports panel.', 'status')],
            }));
          }
        }

      } else {
        // RECITE-WIRING-DELTA — typed failure outcome (success:false on 200):
        // {blocked:true, error_type:'output_blocked', reason} et al. A SYSTEM
        // error notice with the backend reason — never a Rexy-attributed bubble.
        const d = response.data || {};
        const reason = d.reason || d.message || d.error;
        const text = d.blocked || d.error_type === 'output_blocked'
          ? `Governance blocked this ${d.error_type === 'output_blocked' ? 'response' : 'entry'}${reason ? ` — ${reason}` : '.'}`
          : `Request failed${reason ? ` — ${reason}` : '.'}`;
        setAllMessages((prev) => ({
          ...prev,
          [chatKey]: [...(prev[chatKey] || []), systemNotice(text, 'error')],
        }));
      }
    } catch (error: any) {
      // RECITE-WIRING-DELTA — transport/5xx failures carry typed outcomes
      // (502 {error_type:'infrastructure'|'rexy_turn_error', reason}). All are
      // SYSTEM error notices — the interface reports; the agent never voices
      // rate limits, timeouts, or connection errors.
      const status = error?.response?.status;
      const d = error?.response?.data;
      const serverMsg = d?.error;
      const reason = d?.reason || d?.error || d?.detail;
      const isRateLimit = status === 429 || d?.rate_limited;
      const isTimeout = status === 504 || d?.timeout;

      let errorText = 'Could not reach the Rexy server. Is it running?';
      if (isRateLimit && serverMsg) {
        errorText = serverMsg;
      } else if (isTimeout) {
        errorText = 'The agent took too long to respond. Try again.';
      } else if (d?.error_type === 'infrastructure') {
        errorText = `Infrastructure error${reason ? ` — ${reason}` : '.'}`;
      } else if (d?.error_type === 'rexy_turn_error') {
        errorText = `Rexy's turn failed${reason ? ` — ${reason}` : '.'}`;
      } else if (serverMsg) {
        errorText = serverMsg;
      }

      setAllMessages((prev) => ({
        ...prev,
        [chatKey]: [...(prev[chatKey] || []), systemNotice(errorText, 'error')],
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // ── Chunk 6 — UI-state-only nav callbacks ──────────────────────────────────
  // Flag 10: back chevron + Home pill flip `screen` only. They never call
  // handleDomainChange or handleAgentChange; activeDomain / activeAgent (and the
  // backend session they represent) persist across screen changes.
  const handleHomePress = () => { setShowArthurDashboard(false); setScreen('home'); };
  const handleBackPress = () => {
    if (showArthurDashboard) { setShowArthurDashboard(false); return; }
    setScreen((s) => (s === 'agent' ? 'domain' : s === 'domain' ? 'home' : s));
  };
  // Flag 8 + CORRECTION 3: await the existing async handleAgentChange (which
  // POSTs /api/agent, clears thread state, loads threads) BEFORE flipping the
  // screen, so thread state has settled before the agent view mounts.
  // THE ONE ROUTE INTO THE CHAT SCREEN, and it no longer walks through a failure.
  // `setScreen('agent')` used to run whether or not the agent activated, which is
  // how a chat opened with `activeAgent` null and looked completely normal.
  const handleAgentClick = async (agent: string) => {
    const activated = await handleAgentChange(agent);
    if (!activated) return;   // the strip says what happened; the surface stays put
    setScreen('agent');
  };

  // The graph lab (?surface=graphlab) mounts ABOVE the PIN gate — owner ruling,
  // second pass. It is the ONE surface for which pre-auth is correct: it makes no
  // request, holds no token, reads no backend and renders only synthetic `lab:`
  // data, so there is nothing behind it to expose. The gate had to move because
  // the headless capture harness cannot authenticate (and must never hold the
  // PIN), and the seventeen acceptance captures — not an impression of a screen —
  // are the evidence this build is accepted on.
  //
  // G2.0 — THAT RULING COVERS THE SYNTHETIC LAB AND NOTHING ELSE. `&source=live`
  // reads Rexy's real domain and agent topology through an authenticated
  // endpoint, so it falls through to the PIN gate below exactly like every other
  // real surface. The pre-auth bypass is granted to the mode that holds nothing,
  // never to the surface name.
  if (GRAPH_LAB_SURFACE && currentGraphSourceMode() === 'synthetic') {
    return <RexyGraphLab />;
  }

  // PIN screen lives OUTSIDE AppShell (Flag 4: AppShell wraps authenticated
  // screens only; PIN keeps its T-Rex backdrop, never inherits the atmosphere).
  if (!isAuthenticated) {
    return <RexyPinScreen onLogin={handleLogin} error={authError} />;
  }

  // Authenticated live-structure lab. Same component, same document, same token
  // — it simply could not be reached before the PIN.
  if (GRAPH_LAB_SURFACE) {
    return <RexyGraphLab />;
  }

  // SURFACE SELECTION — the portrait phone surface (owner ruling 2026-07-31,
  // option B: ?surface=phone, same origin). It mounts HERE, after the PIN gate,
  // because the session token lives in this document's memory and nowhere else
  // (server/app.py:2133 — header-only auth, never localStorage). A redirect gave
  // the design document its own token-less window, which is why every panel read
  // unavailable. See components/phone/PhoneHost.tsx.
  //
  // On the desktop this guard is false and the code path below is exactly the
  // one it has always been — the reason App.tsx was previously left untouched.
  if (PHONE_SURFACE) {
    return <PhoneHost />;
  }

  const authToken = (window as any).__rexyToken || null;

  // Flag 1 invariant: App.tsx owns every piece of chat state and every chat
  // handler; ChatSurface only renders. `showDashboardBanners` is forced to
  // false by AgentViewScreen (Flag 6 wrapper retirement) — hence the Omit at
  // the type level.
  const chatSurfaceProps: Omit<ChatSurfaceProps, 'showDashboardBanners'> = {
    arthurContextLabel: arthurSessionContext?.mode === 'live'
      ? `Live · ${arthurSessionContext.instrument === 'XAUUSD' ? 'Gold' : 'Nasdaq'} · ${arthurSessionContext.approach === 'main' ? 'Main Strategy' : 'Discretionary Trading'} · ${arthurSessionContext.accountId || 'Account unassigned'}`
      : arthurSessionContext?.mode === 'backtesting' ? `Backtesting · ${arthurSessionContext.backtestName || arthurSessionContext.backtestId}` : arthurSessionContext?.mode === 'demo' ? 'Demo' : arthurSessionContext ? 'General Conversation' : undefined,
    activeDomain,
    activeAgent,
    authToken,
    status,
    messages,
    isLoading,
    threads,
    activeThreadId,
    sidebarOpen,
    setSidebarOpen,
    overrideMode,
    overrideApi,
    showOverrideMenu,
    setShowOverrideMenu,
    routingMode,
    // The escalation toggle replaces setRoutingMode: routing is DERIVED from OS state,
    // never set from the UI. localLaneUp is REPORTED (offline is a state, not a choice).
    escalation,
    escalationLoaded,
    localLaneUp,
    toggleEscalation,
    fallbackTarget,
    setFallbackTarget,
    activeModel,
    setActiveModel,
    showModelMenu,
    setShowModelMenu,
    ALL_MODELS,
    memoryOverride,
    lastMemoryStatus,
    showSecurityDash,
    setShowSecurityDash,
    securityFlags,
    securitySummary,
    securityLevel,
    setSecurityLevel,
    showMemoryManager,
    setShowMemoryManager,
    memoryEntries,
    memoryDomain,
    memoryFilter,
    setMemoryFilter,
    onSendMessage: handleSendMessage,
    onSendWithFile: handleSendWithFile,
    onSendWithFiles: handleSendWithFiles,
    onFileUpload: handleFileUpload,
    onNewThread: handleNewThread,
    onSelectThread: handleSelectThread,
    onEndThread: handleEndThread,
    onRenameThread: handleRenameThread,
    onDeleteThread: handleDeleteThread,
    onAgentChange: handleAgentChange,
    onOverrideToggle: handleOverrideToggle,
    onMemoryOverrideToggle: handleMemoryOverrideToggle,
    onOpenSecurity: handleOpenSecurity,
    onQuit: handleQuit,
    onFetchSecurity: fetchSecurity,
    onFetchMemory: fetchMemory,
    onOpenCodeDashboard: () => setShowCodeDashboard(true),
    onOpenSecurityDashboard: () => setShowSecurityDashboard(true),
    onOpenArthurDashboard: () => setShowArthurDashboard(true),
  };

  // 6.8 — props for the homepage CHAT tab, bound to the {HOMEPAGE, REXY} slice.
  // Spreads the nav props for the shared global controls (model/override/memory),
  // then overrides identity + chat content + handlers with the Rexy slice, and
  // hard-disables uploads (honest no-op handlers + hidden affordance).
  const rexyChatProps: ChatSurfaceProps = {
    ...chatSurfaceProps,
    activeDomain: REXY_HOME_DOMAIN,
    activeAgent: REXY_HOME_AGENT,
    status,
    messages: rexyMessages,
    isLoading: rexyLoading,
    threads: rexyThreads,
    activeThreadId: rexyActiveThreadId,
    sidebarOpen,
    setSidebarOpen,
    onSendMessage: handleRexySend,
    onNewThread: handleRexyNewThread,
    onSelectThread: handleRexySelectThread,
    onEndThread: handleRexyEndThread,
    onRenameThread: handleRexyRenameThread,
    onDeleteThread: handleRexyDeleteThread,
    onFileUpload: rexyNoUpload,
    onSendWithFile: rexyNoUpload,
    onSendWithFiles: rexyNoUpload,
    uploadsDisabled: true,
    showDashboardBanners: false,
  };

  return (
    <AppShell
      screen={screen}
      legacyDashboardOpen={showCodeDashboard || showSecurityDashboard || showArthurDashboard}
      activeDomain={activeDomain}
      activeAgent={activeAgent}
      onHomePress={() => { setShowSettings(false); handleHomePress(); }}
      onDomainPress={(d) => { setShowSettings(false); handleDomainChange(d); setScreen('domain'); }}
      onBackPress={handleBackPress}
      settingsActive={showSettings}
      onSettingsPress={() => setShowSettings(true)}
    >
      {/* ── A HELD DOCUMENT IS WAITING ON ONE ANSWER ──────────────────────────
          Owner correction 2026-08-17. The agent could not tell which panel the
          document belongs to, so it asked and KEPT the document. This bar is
          the answer: one tap files it, and nothing is uploaded twice. It sits
          above every screen because a held document is not a property of the
          view the owner happens to be on.

          IT IS PORTALLED TO document.body, AND THAT IS LOAD-BEARING — owner
          ruling 2026-08-24. As a child of AppShell it rendered inside
          `contentStyle`, which carries `zIndex: 1` and therefore OPENS A
          STACKING CONTEXT. `zIndex: 9999` is only ever 9999 among its
          SIBLINGS: scoped inside that context it could not paint above the
          shell's own later, equal-z-index children — the bottom DomainBar slot
          (zIndex 1), the back chevron (10) or the corner orb (11). A bar
          anchored at bottom:12 sits exactly where the DomainBar is, so the one
          affordance that must outrank every screen was ranked beneath the
          screen switcher. The portal puts it in the ROOT stacking context,
          which is what "above every screen" already claimed.

          A bar covering the domain switcher while a document is held is the
          right order: it is dismissible with the ×, and the document stays
          held. */}
      {createPortal(<>
      {agentError && (
        /* THE CAUSE, NOT THE CONSEQUENCE. A failed activation is upstream of
           everything else this bar reports, so it is shown FIRST and it
           suppresses the held-list strip below — which, with no active agent,
           could only ever say "no active agent" back. */
        <div style={{
          position: 'fixed', left: 12, right: 12, bottom: 12, zIndex: 9999,
          background: '#141414', border: '1px solid #ef4444', borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)', padding: '12px 14px',
          color: '#ededed', fontSize: 13,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontWeight: 600, color: '#ef4444' }}>The agent was not activated</div>
            <button
              onClick={() => setAgentError(null)}
              title="Dismiss"
              style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer' }}
            >&times;</button>
          </div>
          <div style={{ color: '#666', marginTop: 6 }}>{agentError}</div>
        </div>
      )}
      {heldError && !pendingHold && !agentError && (
        /* THE ASK'S ABSENCE, REPORTED IN THE ASK'S OWN PLACE — item 4. It
           renders ONLY when there is no ask to show, so a working chooser is
           never displaced by a stale complaint, and it never writes to message
           state, so it cannot break a conversation. */
        <div style={{
          position: 'fixed', left: 12, right: 12, bottom: 12, zIndex: 9999,
          background: '#141414', border: '1px solid #ef4444', borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)', padding: '12px 14px',
          color: '#ededed', fontSize: 13,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontWeight: 600, color: '#ef4444' }}>Held documents could not be read</div>
            <button
              onClick={() => setHeldError(null)}
              title="Dismiss — nothing held is lost"
              style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer' }}
            >&times;</button>
          </div>
          <div style={{ color: '#666', marginTop: 6 }}>{heldError}</div>
        </div>
      )}
      {pendingHold && (
        <div style={{
          position: 'fixed', left: 12, right: 12, bottom: 12, zIndex: 9999,
          background: '#141414', border: '1px solid #2a2a2a', borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)', padding: '12px 14px',
          color: '#ededed', fontSize: 13,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontWeight: 600 }}>
              Holding {pendingHold.filename || 'a document'} — where does it go?
            </div>
            <button
              onClick={() => setPendingHold(null)}
              title="Hide for now — the document stays held"
              style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer' }}
            >×</button>
          </div>
          <div style={{ color: '#666', margin: '6px 0 10px' }}>{pendingHold.question}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(pendingHold.offer || []).map((t: any) => (
              (t.slots && t.slots.length ? t.slots : [{ slot: null, label: t.tab }]).map((s: any) => (
                <button
                  key={`${t.folder}_${s.slot || 'tab'}`}
                  onClick={() => answerPlacement(t.folder, s.slot)}
                  style={{
                    background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: 8,
                    color: '#ededed', padding: '6px 10px', fontSize: 12, cursor: 'pointer',
                  }}
                >
                  {t.slots && t.slots.length ? `${t.tab} → ${s.label}` : t.tab}
                </button>
              ))
            ))}
          </div>
          {/* AN ASK WITH NOTHING TO TAP IS THE SAME SILENCE ONE LAYER IN. The
              chooser's buttons come from the skeleton's `offer`; when that
              comes back empty the bar used to render a title and a question
              above an EMPTY row, which is a heading with no body — the exact
              shape already logged this month. It says so, and the input below
              is the answer — the chips were never the mechanism. */}
          {!(pendingHold.offer || []).length && (
            <div style={{ color: '#f59e0b', fontSize: 12 }}>
              No panels were offered for this document. Name where it goes below —
              the panel does not have to exist yet.
            </div>
          )}

          {/* ── THE FREE-TEXT ANSWER ──────────────────────────────────────────
              THE UPLOAD IS THE DECLARATION: `folder` accepts any name, nothing
              validates it against the offer, and write_doctrine declares the
              folder itself before placing. So this is not a fallback for when
              the chips are missing — it is the mechanism, and the chips are the
              shortcut. It is always shown, because an offer that happens to be
              non-empty is not evidence that one of its entries is the right
              answer. */}
          <form
            onSubmit={(e) => { e.preventDefault(); if (folderDraft.trim()) answerPlacement(folderDraft, null); }}
            style={{ display: 'flex', gap: 6, marginTop: 10 }}
          >
            <input
              value={folderDraft}
              onChange={(e) => setFolderDraft(e.target.value)}
              disabled={filing}
              placeholder="Name the panel — 00_SYSTEM, or a path: Regions / Maps"
              aria-label="Where this document goes"
              style={{
                flex: 1, minWidth: 0, background: '#0a0a0a', color: '#ededed',
                border: '1px solid #2a2a2a', borderRadius: 8,
                padding: '7px 10px', fontSize: 12, outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={filing || !folderDraft.trim()}
              style={{
                background: folderDraft.trim() && !filing ? '#0070f3' : '#0a0a0a',
                border: '1px solid ' + (folderDraft.trim() && !filing ? '#0070f3' : '#2a2a2a'),
                borderRadius: 8, color: folderDraft.trim() && !filing ? '#ededed' : '#666',
                padding: '7px 14px', fontSize: 12,
                cursor: filing || !folderDraft.trim() ? 'default' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {filing ? 'Filing…' : 'File it'}
            </button>
            <button
              type="button"
              onClick={() => (confirmDiscard ? discardHeld() : setConfirmDiscard(true))}
              onBlur={() => setConfirmDiscard(false)}
              disabled={filing}
              title="Delete the held document without filing it"
              style={{
                background: confirmDiscard ? '#ef4444' : '#0a0a0a',
                border: '1px solid ' + (confirmDiscard ? '#ef4444' : '#2a2a2a'),
                borderRadius: 8, color: confirmDiscard ? '#ededed' : '#666',
                padding: '7px 14px', fontSize: 12,
                cursor: filing ? 'default' : 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {confirmDiscard ? 'Discard it' : 'Discard'}
            </button>
          </form>

          {/* THE DOOR'S REFUSAL, WHERE THE ANSWER WAS TYPED. The chat keeps its
              own notice — that is the transcript — but the correction happens
              here, so the reason has to be here too. */}
          {placementError && (
            <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>
              Still held — {placementError}
            </div>
          )}
        </div>
      )}
      </>, document.body)}

      {/* Legacy fullscreen dashboard branches — top-priority safety net
          (Flag 5: rexy-preview auto-open + any external trigger still works).
          SETTINGS SITS BELOW THESE, DELIBERATELY. It is a page, not a takeover: with
          it above, a rexy-preview (which sets showCodeDashboard) or any other
          external dashboard trigger would fire while Settings stayed rendered and the
          safety net would never display. */}
      {showCodeDashboard ? (
        <CodePage
          authToken={authToken}
          initialPreview={previewPayload}
          onBack={() => { setShowCodeDashboard(false); setPreviewPayload(null); }}
        />
      ) : showSecurityDashboard ? (
        <SecurityPage
          authToken={authToken}
          onBack={() => setShowSecurityDashboard(false)}
        />
      ) : showArthurDashboard ? (
        <TradingPage
          authToken={authToken}
          onBack={() => setShowArthurDashboard(false)}
          threadId={activeThreadId}
          onActiveSurfaceChange={setArthurActiveSurface}
          onSessionContextChange={setArthurSessionContext}
        />
      ) : showSettings ? (
        /* SETTINGS — a VIEW that replaces the domain content, ranked BELOW the
           fullscreen safety net above and ABOVE the ordinary screens below.
           `activeDomain` is untouched while it is open, so the same page is reachable
           from every domain and no domain conditional anywhere behaves differently.
           It is not a tenth domain and never enters the DOMAINS array. */
        <SettingsPage />
      ) : screen === 'agent' ? (
        <AgentViewScreen
          domain={activeDomain}
          agent={activeAgent}
          authToken={authToken}
          activeThreadId={activeThreadId}
          onArthurSurfaceChange={setArthurActiveSurface}
          onArthurSessionContextChange={setArthurSessionContext}
          chatSurfaceProps={chatSurfaceProps}
          // On a full-surface domain the agent view is only ever reached by
          // asking for CHAT by name, and DASHBOARD goes back to the agent's real
          // dashboard — its Mission Control root. activeAgent is still set, so
          // the surface reopens on that agent's own page.
          initialTab={dashboardIsMissionControl(activeDomain) ? 'chat' : undefined}
          onDashboard={dashboardIsMissionControl(activeDomain)
            ? () => setScreen('domain') : undefined}
        />
      ) : screen === 'domain' ? (
        <DomainDashboardScreen
          domain={activeDomain}
          activeAgent={activeAgent}
          onAgentClick={handleAgentClick}
          authToken={authToken}
          onBack={handleBackPress}
          nav={missionNav}
          onNavChange={setMissionNav}
        />
      ) : (
        <RexyHomepage rexyChat={rexyChatProps} />
      )}
    </AppShell>
  );
};

export default App;
